<template>
  <main :class="['p5-page', localeClass]">
    <nav class="p5-subnav" :aria-label="t('nav.label')">
      <div class="p5-subnav__inner">
        <a class="p5-subnav__brand" href="#p5-hero" @click.prevent="scrollToSection('p5-hero')">{{ subnavBrand }}</a>
        <div class="p5-subnav__links">
          <a
            v-for="item in sectionLinks"
            :key="item.id"
            :href="`#${item.id}`"
            :class="{ active: activeSection === item.id }"
            @click.prevent="scrollToSection(item.id)"
          >
            {{ t(item.labelKey) }}
          </a>
        </div>
        <div class="p5-subnav__actions">
          <a class="p5-subnav__cta" href="#contact-modal" @click.prevent="openContactModal">{{ t('nav.requestDemo') }}</a>
        </div>
      </div>
    </nav>

    <section
      id="p5-hero"
      class="p5-hero"
      :style="{
        '--p5-hero-scale': heroScale,
        '--p5-hero-copy-top': heroCopyTop
      }"
      aria-labelledby="p5-hero-title"
    >
      <div class="p5-hero__canvas">
        <template v-if="isMobileLayout">
          <img
            class="p5-hero__background"
            src="/static/images/p5/hero-background-2k.webp"
            alt=""
            fetchpriority="high"
          >
          <img
            class="p5-hero__product"
            src="/static/images/p5/hero-product-2k.webp"
            :alt="t('accessibility.heroProductAlt')"
            fetchpriority="high"
          >
        </template>
        <template v-else>
          <img
            class="p5-hero__poster"
            src="/static/images/p5/p5-hero-banner-poster.webp"
            alt=""
            aria-hidden="true"
            fetchpriority="high"
          >
          <video
            ref="heroVideo"
            class="p5-hero__video"
            :class="{ 'p5-hero__video--hidden': reducedMotion || heroVideoPlaybackFailed }"
            poster="/static/images/p5/p5-hero-banner-poster.webp"
            autoplay
            muted
            loop
            playsinline
            preload="metadata"
            aria-hidden="true"
            @playing="handleHeroVideoPlaying"
            @error="handleHeroVideoError"
          >
            <source
              src="https://zkfilecenter.oss-cn-hangzhou.aliyuncs.com/dev/service-ticket/problem-video/20260804/233413881806680.mp4"
              type="video/mp4"
            >
            <source src="/static/images/p5/p5-hero-banner.mp4" type="video/mp4">
          </video>
        </template>
        <div class="p5-hero__copy">
          <p>{{ t('hero.eyebrow') }}</p>
          <h1 id="p5-hero-title">{{ t('hero.title') }}</h1>
        </div>
      </div>
    </section>

    <section ref="upgradeSection" id="upgrade" class="p5-section p5-upgrade" aria-labelledby="upgrade-title">
      <header class="p5-section-heading">
        <p>{{ t('upgrade.eyebrow') }}</p>
        <h2 id="upgrade-title">{{ t('upgrade.title') }}</h2>
      </header>

      <div v-if="isMobileLayout" class="p5-mobile-story p5-mobile-upgrade-list">
        <article
          v-for="item in mobileUpgradeItems"
          :key="`mobile-upgrade-${item.key}`"
          class="p5-mobile-upgrade-item"
          :class="`p5-mobile-upgrade-item--${item.key}`"
        >
          <p class="p5-mobile-story__label">{{ t(item.titleKey) }}</p>
          <div class="p5-mobile-upgrade-item__visual">
            <img :src="item.image" :alt="t(item.altKey)" loading="lazy" decoding="async">
          </div>
          <div class="p5-mobile-story__copy">
            <h3 v-for="headlineKey in item.headlineKeys" :key="headlineKey">{{ t(headlineKey) }}</h3>
            <ul>
              <li v-for="pointKey in item.pointKeys" :key="pointKey">{{ t(pointKey) }}</li>
            </ul>
          </div>
        </article>
      </div>

      <div v-else class="p5-story-track">
        <div class="p5-story-viewport">
          <div ref="upgradeStage" class="p5-upgrade-stage">
          <div class="p5-upgrade-controls" :aria-label="t('accessibility.upgradeControls')">
            <button
              type="button"
              :aria-label="t('accessibility.upgradePrevious')"
              :disabled="activeUpgradeIndex === 0"
              @click="changeActiveUpgrade(-1)"
            >
              <span aria-hidden="true">↑</span>
            </button>
            <button
              type="button"
              :aria-label="t('accessibility.upgradeNext')"
              :disabled="activeUpgradeIndex === upgradeItems.length - 1"
              @click="changeActiveUpgrade(1)"
            >
              <span aria-hidden="true">↓</span>
            </button>
          </div>

          <div class="p5-upgrade-accordion">
            <div class="p5-upgrade-accordion__items">
              <article
                v-for="(item, index) in upgradeItems"
                :key="item.key"
                class="p5-upgrade-accordion__item"
                :class="{ active: activeUpgradeIndex === index }"
              >
                <button
                  :id="`upgrade-trigger-${item.key}`"
                  class="p5-upgrade-accordion__trigger"
                  type="button"
                  :aria-expanded="activeUpgradeIndex === index"
                  :aria-controls="`upgrade-panel-${item.key}`"
                  @click="setActiveUpgrade(index)"
                  @keydown="handleUpgradeKeydown"
                >
                  <span>{{ t(item.titleKey) }}</span>
                  <span class="p5-upgrade-accordion__symbol" aria-hidden="true">
                    {{ activeUpgradeIndex === index ? '−' : '+' }}
                  </span>
                </button>

                <div
                  v-show="activeUpgradeIndex === index"
                  :id="`upgrade-panel-${item.key}`"
                  class="p5-upgrade-accordion__panel"
                  :class="{ 'p5-upgrade-accordion__panel--motion': !reducedMotion }"
                  role="region"
                  :aria-labelledby="`upgrade-trigger-${item.key}`"
                >
                  <img
                    v-if="isUpgradeViewportReady && activeUpgradeIndex === index"
                    :key="`mobile-${item.key}-${is4kUpgradeViewport ? '4k' : 'standard'}`"
                    class="p5-upgrade-accordion__mobile-image"
                    :class="`p5-upgrade-image--${item.key}`"
                    :src="getUpgradeImage(item)"
                    :alt="t(item.altKey)"
                    decoding="async"
                    @load="handleUpgradeImageLoad"
                  >
                  <div class="p5-upgrade-accordion__headlines">
                    <h3 v-for="headlineKey in item.headlineKeys" :key="headlineKey">{{ t(headlineKey) }}</h3>
                  </div>
                  <ul>
                    <li v-for="pointKey in item.pointKeys" :key="pointKey">{{ t(pointKey) }}</li>
                  </ul>
                </div>
              </article>
            </div>
          </div>

          <div class="p5-upgrade-visual">
            <img
              v-if="isUpgradeViewportReady"
              :key="`${activeUpgrade.key}-${is4kUpgradeViewport ? '4k' : 'standard'}`"
              :class="[
                `p5-upgrade-image--${activeUpgrade.key}`,
                { 'p5-upgrade-visual__image--motion': !reducedMotion }
              ]"
              :src="activeUpgradeImage"
              :alt="t(activeUpgrade.altKey)"
              decoding="async"
              @load="handleUpgradeImageLoad"
            >
          </div>
          </div>
        </div>
      </div>
    </section>

    <section id="features" class="p5-section p5-features p5-fixed-section" aria-labelledby="features-title">
      <header class="p5-section-heading p5-fixed-section__inner">
        <p>{{ t('core.eyebrow') }}</p>
        <h2 id="features-title">{{ t('core.title') }}</h2>
      </header>

      <div class="p5-feature-grid p5-fixed-section__content">
        <article class="p5-feature-card p5-feature-card--bia">
          <img src="/static/images/p5/core-bia.png" alt="" loading="lazy">
          <h3>{{ t('core.cards.bodyComposition') }}</h3>
        </article>

        <article class="p5-feature-card p5-feature-card--posture">
          <img src="/static/images/p5/core-posture.png" alt="" loading="lazy">
          <div class="p5-feature-card__copy">
            <p>{{ t('core.cards.aiVision') }}</p>
            <h3>{{ t('core.cards.posture') }}</h3>
          </div>
        </article>

        <article class="p5-feature-card p5-feature-card--agility">
          <div class="p5-agility-visual" aria-hidden="true">
            <img src="/static/images/p5/core-agility-left.svg" alt="">
            <img src="/static/images/p5/core-agility-center.svg" alt="">
            <img src="/static/images/p5/core-agility-right.svg" alt="">
            <img src="/static/images/p5/core-agility-left-active.svg" alt="">
            <img src="/static/images/p5/core-agility-center-active.svg" alt="">
            <img src="/static/images/p5/core-agility-right-active.svg" alt="">
            <img src="/static/images/p5/core-agility-left.svg" alt="">
            <img src="/static/images/p5/core-agility-center.svg" alt="">
            <img src="/static/images/p5/core-agility-right.svg" alt="">
          </div>
          <h3>{{ t('core.cards.agility') }}</h3>
        </article>

        <article class="p5-feature-card p5-feature-card--balance">
          <img src="/static/images/p5/core-balance-performance.png" alt="" loading="lazy">
          <h3>{{ t('core.cards.balance') }}</h3>
        </article>

        <article class="p5-feature-card p5-feature-card--blood">
          <img src="/static/images/p5/core-blood-pressure.png" alt="" loading="lazy">
          <h3>{{ t('core.cards.bloodPressure') }}</h3>
        </article>

        <article class="p5-feature-card p5-feature-card--vital">
          <img src="/static/images/p5/core-vital-capacity.png" alt="" loading="lazy">
          <h3>{{ t('core.cards.vitalCapacity') }}</h3>
        </article>
      </div>
    </section>

    <section id="design" class="p5-section p5-design p5-fixed-section" aria-labelledby="design-title">
      <header class="p5-section-heading p5-fixed-section__inner">
        <p>{{ t('design.eyebrow') }}</p>
        <h2 id="design-title">{{ t('design.title') }}</h2>
      </header>

      <article
        v-for="item in designItems"
        :key="item.key"
        class="p5-design-card p5-fixed-section__inner"
        :class="`p5-design-card--${item.key}`"
      >
        <div class="p5-design-card__copy">
          <h3 v-if="item.key === 'screen'">{{ t(item.titleKey).split('4K')[0] }}<span class="p5-screen-title__highlight">4K</span>{{ t(item.titleKey).split('4K')[1] }}</h3>
          <h3 v-else>{{ t(item.titleKey) }}</h3>
          <p>{{ t(item.descriptionKey) }}</p>
        </div>
        <img :src="isChinese && item.imageCn ? item.imageCn : item.image" :alt="t(item.altKey)" loading="lazy">
      </article>
    </section>

    <section ref="serviceSection" id="services" class="p5-section p5-services" aria-labelledby="services-title">
      <header class="p5-section-heading">
        <p>{{ t('services.eyebrow') }}</p>
        <h2 id="services-title">{{ t('services.title') }}</h2>
      </header>

      <div v-if="isMobileLayout" class="p5-mobile-story p5-mobile-service-list">
        <article
          v-for="service in serviceItems"
          :key="`mobile-service-${service.key}`"
          class="p5-mobile-service-item"
          :class="`p5-mobile-service-item--${service.key}`"
        >
          <div class="p5-mobile-service-item__visual">
            <div v-if="service.key === 'platform'" class="p5-mobile-service-platform">
              <img
                :src="service.desktopImage"
                :alt="t('accessibility.serviceAlt.platformDesktop')"
                loading="lazy"
                decoding="async"
              >
              <img
                :src="service.mobileImage"
                :alt="t('accessibility.serviceAlt.platformMobile')"
                loading="lazy"
                decoding="async"
              >
            </div>
            <img
              v-else
              :src="service.image"
              :alt="t(service.altKey)"
              loading="lazy"
              decoding="async"
            >
          </div>
          <div class="p5-mobile-story__copy">
            <h3>{{ t(service.titleKey) }}</h3>
            <ul>
              <li v-for="pointKey in service.pointKeys" :key="pointKey">{{ t(pointKey) }}</li>
            </ul>
          </div>
        </article>
      </div>

      <div v-else class="p5-story-track">
        <div class="p5-story-viewport">
          <div ref="serviceStage" class="p5-service-stage">
          <div class="p5-service-controls" :aria-label="t('accessibility.serviceControls')">
            <button
              type="button"
              :aria-label="t('accessibility.servicePrevious')"
              :disabled="activeServiceIndex === 0"
              @click="changeActiveService(-1)"
            >
              <span aria-hidden="true">↑</span>
            </button>
            <button
              type="button"
              :aria-label="t('accessibility.serviceNext')"
              :disabled="activeServiceIndex === serviceItems.length - 1"
              @click="changeActiveService(1)"
            >
              <span aria-hidden="true">↓</span>
            </button>
          </div>

          <div class="p5-service-accordion">
            <div class="p5-service-accordion__items">
              <article
                v-for="(service, index) in serviceItems"
                :key="service.key"
                class="p5-service-accordion__item"
                :class="{ active: activeServiceIndex === index }"
              >
                <button
                  :id="`service-trigger-${service.key}`"
                  class="p5-service-accordion__trigger"
                  type="button"
                  :aria-expanded="activeServiceIndex === index"
                  :aria-controls="`service-panel-${service.key}`"
                  @click="setActiveService(index)"
                  @keydown="handleServiceKeydown"
                >
                  <span>{{ t(service.titleKey) }}</span>
                  <span class="p5-service-accordion__symbol" aria-hidden="true">
                    {{ activeServiceIndex === index ? '−' : '+' }}
                  </span>
                </button>

                <div
                  v-show="activeServiceIndex === index"
                  :id="`service-panel-${service.key}`"
                  class="p5-service-accordion__panel"
                  :class="{ 'p5-service-accordion__panel--motion': !reducedMotion }"
                  role="region"
                  :aria-labelledby="`service-trigger-${service.key}`"
                >
                  <div
                    class="p5-service-accordion__mobile-visual"
                    :class="`p5-service-visual--${service.key}`"
                  >
                    <div
                      v-if="isUpgradeViewportReady && activeServiceIndex === index && service.key === 'platform'"
                      class="p5-service-visual__platform"
                    >
                      <img
                        :src="getServicePlatformImage('desktop')"
                        :alt="t('accessibility.serviceAlt.platformDesktop')"
                        decoding="async"
                        @load="handleServiceImageLoad"
                      >
                      <img
                        :src="getServicePlatformImage('mobile')"
                        :alt="t('accessibility.serviceAlt.platformMobile')"
                        decoding="async"
                        @load="handleServiceImageLoad"
                      >
                    </div>
                    <img
                      v-else-if="isUpgradeViewportReady && activeServiceIndex === index"
                      class="p5-service-visual__image"
                      :src="getServiceImage(service)"
                      :alt="t(service.altKey)"
                      decoding="async"
                      @load="handleServiceImageLoad"
                    >
                  </div>
                  <ul>
                    <li v-for="pointKey in service.pointKeys" :key="pointKey">{{ t(pointKey) }}</li>
                  </ul>
                </div>
              </article>
            </div>
          </div>

          <div class="p5-service-visual">
            <div
              v-if="isUpgradeViewportReady"
              :key="`${activeService.key}-${is4kUpgradeViewport ? '4k' : 'standard'}`"
              class="p5-service-visual__content"
              :class="[
                `p5-service-visual--${activeService.key}`,
                { 'p5-service-visual__content--motion': !reducedMotion }
              ]"
            >
              <div v-if="activeService.key === 'platform'" class="p5-service-visual__platform">
                <img
                  :src="getServicePlatformImage('desktop')"
                  :alt="t('accessibility.serviceAlt.platformDesktop')"
                  decoding="async"
                  @load="handleServiceImageLoad"
                >
                <img
                  :src="getServicePlatformImage('mobile')"
                  :alt="t('accessibility.serviceAlt.platformMobile')"
                  decoding="async"
                  @load="handleServiceImageLoad"
                >
              </div>
              <img
                v-else
                class="p5-service-visual__image"
                :src="activeServiceImage"
                :alt="t(activeService.altKey)"
                decoding="async"
                @load="handleServiceImageLoad"
              >
            </div>
          </div>
          </div>
        </div>
      </div>
    </section>

    <section id="comparison" class="p5-section p5-comparison p5-fixed-section" aria-labelledby="comparison-title">
      <header class="p5-comparison__heading p5-fixed-section__inner">
        <h2 id="comparison-title">{{ t('comparison.title') }}</h2>
      </header>

      <div class="p5-comparison__panel p5-fixed-section__content">
        <article class="p5-product-card">
          <img
            :src="fixedComparisonProduct.image"
            :alt="formatMessage('accessibility.comparisonProductAlt', { model: fixedComparisonProduct.name })"
            loading="lazy"
          >
          <div class="p5-product-card__model">
            <h3>{{ fixedComparisonProduct.name }}</h3>
          </div>

          <ul class="p5-product-card__functions">
            <li
              v-for="feature in comparisonFeatureRows"
              :key="feature.key"
              :class="{ 'is-unsupported': !fixedComparisonProduct.features.includes(feature.key) }"
            >
              {{ fixedComparisonProduct.features.includes(feature.key) ? t(feature.labelKey) : '-' }}
            </li>
          </ul>

          <dl>
            <div v-for="specification in comparisonSpecifications" :key="specification.key">
              <dt>{{ t(specification.labelKey) }}</dt>
              <dd>{{ t(fixedComparisonProduct.specificationKeys[specification.key]) }}</dd>
            </div>
          </dl>
        </article>

        <article class="p5-product-card p5-product-card--selectable">
          <img
            :src="selectedComparisonProduct.image"
            :alt="formatMessage('accessibility.comparisonProductAlt', { model: selectedComparisonProduct.name })"
            loading="lazy"
          >
          <div ref="comparisonSelector" class="p5-product-card__model p5-comparison-selector">
            <h3>{{ selectedComparisonProduct.name }}</h3>
            <button
              ref="comparisonMenuButton"
              class="p5-comparison-selector__trigger"
              type="button"
              aria-haspopup="listbox"
              aria-controls="comparison-product-options"
              :aria-expanded="isComparisonMenuOpen ? 'true' : 'false'"
              :aria-label="t('accessibility.comparisonChoose')"
              @click="toggleComparisonMenu"
              @keydown="handleComparisonTriggerKeydown"
            >
              <span aria-hidden="true"></span>
            </button>

            <div
              v-if="isComparisonMenuOpen"
              id="comparison-product-options"
              class="p5-comparison-selector__menu"
              role="listbox"
              :aria-label="t('accessibility.comparisonList')"
            >
              <button
                v-for="(product, index) in comparisonOptions"
                :id="`comparison-option-${product.key}`"
                :key="product.key"
                ref="comparisonOptionButtons"
                class="p5-comparison-selector__option"
                :class="{ 'is-selected': product.key === selectedComparisonKey }"
                type="button"
                role="option"
                :aria-selected="product.key === selectedComparisonKey ? 'true' : 'false'"
                @click="selectComparisonProduct(product.key)"
                @keydown="handleComparisonOptionKeydown($event, index)"
              >
                {{ product.name }}
              </button>
            </div>
          </div>

          <ul class="p5-product-card__functions">
            <li
              v-for="feature in comparisonFeatureRows"
              :key="feature.key"
              :class="{ 'is-unsupported': !selectedComparisonProduct.features.includes(feature.key) }"
            >
              {{ selectedComparisonProduct.features.includes(feature.key) ? t(feature.labelKey) : '-' }}
            </li>
          </ul>

          <dl>
            <div v-for="specification in comparisonSpecifications" :key="specification.key">
              <dt>{{ t(specification.labelKey) }}</dt>
              <dd>{{ t(selectedComparisonProduct.specificationKeys[specification.key]) }}</dd>
            </div>
          </dl>
        </article>
      </div>
    </section>

    <section v-if="showSubscribe" class="p5-subscribe-section p5-fixed-section" aria-labelledby="subscribe-title">
      <div class="p5-subscribe p5-fixed-section__inner">
        <h2 id="subscribe-title">{{ t('subscribe.title') }}</h2>
        <p>{{ t('subscribe.description') }}</p>
        <div class="p5-subscribe__field" aria-describedby="subscribe-unavailable">
          <span>{{ t('subscribe.placeholder') }}</span>
          <button type="button" disabled :aria-label="t('subscribe.unavailable')">✉</button>
        </div>
        <span id="subscribe-unavailable" class="sr-only">{{ t('subscribe.unavailable') }}</span>
      </div>
    </section>

    <section class="p5-disclaimer-section p5-fixed-section" aria-labelledby="disclaimer-title">
      <div class="p5-disclaimer p5-fixed-section__content">
        <h2 id="disclaimer-title">{{ t('disclaimer.title') }}</h2>
        <p v-for="(paragraph, index) in currentMessages.disclaimer.paragraphs" :key="index">{{ paragraph }}</p>
      </div>
    </section>
  </main>
</template>

<script>
import { P5_DEFAULT_LOCALE, P5_MESSAGES } from './p5-i18n'
const { siteLanguageMixin } = require('@/utils/site-language.js')
const { requestContactModal } = require('@/utils/contact-modal.cjs')

export default {
  name: 'P5',
  mixins: [siteLanguageMixin],
  data() {
    const hasWindow = typeof window !== 'undefined'
    const upgrade4kMediaQuery = hasWindow
      ? window.matchMedia('(min-width: 3000px) and (min-height: 1600px)')
      : null
    const mobileLayoutMediaQuery = hasWindow
      ? window.matchMedia('(max-width: 768px)')
      : null

    return {
      showSubscribe: false,
      previousDocumentLang: '',
      previousDocumentTitle: '',
      missingP5TranslationWarnings: new Set(),
      activeSection: '',
      heroScale: 1,
      heroCopyTop: '60px',
      heroVideoPlaybackFailed: false,
      activeUpgradeIndex: 0,
      activeServiceIndex: 0,
      isMobileLayout: mobileLayoutMediaQuery ? mobileLayoutMediaQuery.matches : false,
      mobileLayoutMediaQuery,
      isUpgradeViewportReady: hasWindow,
      is4kUpgradeViewport: upgrade4kMediaQuery ? upgrade4kMediaQuery.matches : false,
      upgrade4kMediaQuery,
      upgradeWheelLocked: false,
      upgradeWheelUnlockTimer: null,
      serviceWheelLocked: false,
      serviceWheelUnlockTimer: null,
      upgradePreloadIdleId: null,
      upgradePreloadTimer: null,
      upgradePreloadImages: [],
      upgradePreloadedSources: new Set(),
      servicePreloadIdleId: null,
      servicePreloadTimer: null,
      servicePreloadImages: [],
      servicePreloadedSources: new Set(),
      scrollRafId: null,
      anchorScrollSection: '',
      anchorScrollTimer: null,
      reducedMotion: false,
      reducedMotionQuery: null,
      selectedComparisonKey: 'p3',
      isComparisonMenuOpen: false,
      comparisonFocusIndex: 0,
      sectionLinks: [
        { id: 'upgrade', labelKey: 'nav.upgrade' },
        { id: 'features', labelKey: 'nav.features' },
        { id: 'design', labelKey: 'nav.design' },
        { id: 'services', labelKey: 'nav.services' },
        { id: 'comparison', labelKey: 'nav.comparison' }
      ],
      upgradeItems: [
        {
          key: 'lower',
          titleKey: 'upgrade.lower.title',
          headlineKeys: [
            'upgrade.lower.headlines.0',
            'upgrade.lower.headlines.1'
          ],
          pointKeys: [
            'upgrade.lower.points.0',
            'upgrade.lower.points.1',
            'upgrade.lower.points.2'
          ],
          image: '/static/images/p5/upgrade-lower.png',
          image4k: '/static/images/p5/upgrade-lower-4k.webp',
          altKey: 'accessibility.upgradeAlt.lower'
        },
        {
          key: 'upper',
          titleKey: 'upgrade.upper.title',
          headlineKeys: ['upgrade.upper.headlines.0'],
          pointKeys: [
            'upgrade.upper.points.0',
            'upgrade.upper.points.1',
            'upgrade.upper.points.2'
          ],
          image: '/static/images/p5/upgrade-upper.png',
          image4k: '/static/images/p5/upgrade-upper-4k.webp',
          altKey: 'accessibility.upgradeAlt.upper'
        },
        {
          key: 'grip',
          titleKey: 'upgrade.grip.title',
          headlineKeys: ['upgrade.grip.headlines.0'],
          pointKeys: [
            'upgrade.grip.points.0',
            'upgrade.grip.points.1',
            'upgrade.grip.points.2'
          ],
          image: '/static/images/p5/upgrade-grip.png',
          image4k: '/static/images/p5/upgrade-grip-4k.webp',
          altKey: 'accessibility.upgradeAlt.grip'
        }
      ],
      designItems: [
        {
          key: 'chassis',
          titleKey: 'design.chassis.title',
          descriptionKey: 'design.chassis.description',
          image: '/static/images/p5/design-chassis.png',
          altKey: 'accessibility.designAlt.chassis'
        },
        {
          key: 'screen',
          titleKey: 'design.screen.title',
          descriptionKey: 'design.screen.description',
          image: '/static/images/p5/design-touch-screen-cn-hd.png',
          imageCn: '/static/images/p5/design-touch-screen-cn-hd.png',
          altKey: 'accessibility.designAlt.screen'
        },
        {
          key: 'turntable',
          titleKey: 'design.turntable.title',
          descriptionKey: 'design.turntable.description',
          image: '/static/images/p5/design-electrode-turntable.png',
          altKey: 'accessibility.designAlt.turntable'
        },
        {
          key: 'contours',
          titleKey: 'design.contours.title',
          descriptionKey: 'design.contours.description',
          image: '/static/images/p5/design-curved-contours.png',
          altKey: 'accessibility.designAlt.contours'
        }
      ],
      serviceItems: [
        {
          key: 'reports',
          titleKey: 'services.reports.title',
          pointKeys: [
            'services.reports.points.0',
            'services.reports.points.1'
          ],
          image: '/static/images/p5/service-reports.png',
          image4k: '/static/images/p5/service-reports-4k.webp',
          altKey: 'accessibility.serviceAlt.reports'
        },
        {
          key: 'trends',
          titleKey: 'services.trends.title',
          pointKeys: [
            'services.trends.points.0',
            'services.trends.points.1',
            'services.trends.points.2'
          ],
          image: '/static/images/p5/service-trends.png',
          image4k: '/static/images/p5/service-trends-4k.webp',
          altKey: 'accessibility.serviceAlt.trends'
        },
        {
          key: 'platform',
          titleKey: 'services.platform.title',
          pointKeys: [
            'services.platform.points.0',
            'services.platform.points.1',
            'services.platform.points.2'
          ],
          desktopImage: '/static/images/p5/service-platform-desktop.png',
          desktopImage4k: '/static/images/p5/service-platform-desktop-4k.webp',
          mobileImage: '/static/images/p5/service-platform-mobile.png',
          mobileImage4k: '/static/images/p5/service-platform-mobile-4k.webp'
        }
      ],
      comparisonFeatureRows: [
        { key: 'body-composition', labelKey: 'comparison.features.bodyComposition' },
        { key: 'posture-dimensions', labelKey: 'comparison.features.postureDimensions' },
        { key: 'exercise-safety', labelKey: 'comparison.features.exerciseSafety' },
        { key: 'cardiorespiratory', labelKey: 'comparison.features.cardiorespiratory' },
        { key: 'agility-reaction', labelKey: 'comparison.features.agilityReaction' },
        { key: 'balance', labelKey: 'comparison.features.balance' },
        { key: 'lower-body-power', labelKey: 'comparison.features.lowerBodyPower' },
        { key: 'upper-body-power', labelKey: 'comparison.features.upperBodyPower' },
        { key: 'grip-strength', labelKey: 'comparison.features.gripStrength' },
        { key: 'lung-capacity', labelKey: 'comparison.features.lungCapacity' },
        { key: 'shoulder-range', labelKey: 'comparison.features.shoulderRange' }
      ],
      comparisonSpecifications: [
        { key: 'frequencies', labelKey: 'comparison.specs.frequencies' },
        { key: 'display', labelKey: 'comparison.specs.display' },
        { key: 'workingDistance', labelKey: 'comparison.specs.workingDistance' }
      ],
      fixedComparisonProduct: {
        key: 'p5',
        name: 'P5',
        image: '/static/images/p5/compare-p5.png',
        features: [
          'body-composition',
          'posture-dimensions',
          'exercise-safety',
          'cardiorespiratory',
          'agility-reaction',
          'balance',
          'lower-body-power',
          'upper-body-power',
          'grip-strength',
          'lung-capacity'
        ],
        specificationKeys: {
          frequencies: 'comparison.products.p5.frequencies',
          display: 'comparison.products.p5.display',
          workingDistance: 'comparison.products.p5.workingDistance'
        }
      },
      comparisonOptions: [
        {
          key: 'p3',
          name: 'P3',
          image: '/static/images/p5/compare-p3.png',
          features: [
            'body-composition',
            'posture-dimensions',
            'exercise-safety',
            'cardiorespiratory',
            'agility-reaction',
            'balance',
            'shoulder-range'
          ],
          specificationKeys: {
            frequencies: 'comparison.products.p3.frequencies',
            display: 'comparison.products.p3.display',
            workingDistance: 'comparison.products.p3.workingDistance'
          }
        },
        {
          key: 'p2',
          name: 'P2',
          image: '/static/images/p5/compare-p2.png',
          features: [
            'body-composition',
            'posture-dimensions',
            'shoulder-range'
          ],
          specificationKeys: {
            frequencies: 'comparison.products.p2.frequencies',
            display: 'comparison.products.p2.display',
            workingDistance: 'comparison.products.p2.workingDistance'
          }
        },
        {
          key: 'p1',
          name: 'P1',
          image: '/static/images/p5/compare-p1.png',
          features: [
            'body-composition',
            'posture-dimensions',
            'balance'
          ],
          specificationKeys: {
            frequencies: 'comparison.products.p1.frequencies',
            display: 'comparison.products.p1.display',
            workingDistance: 'comparison.products.p1.workingDistance'
          }
        },
        {
          key: 'p0',
          name: 'P0',
          image: '/static/images/p5/compare-p0.png',
          features: [
            'body-composition'
          ],
          specificationKeys: {
            frequencies: 'comparison.products.p0.frequencies',
            display: 'comparison.products.p0.display',
            workingDistance: 'comparison.products.p0.workingDistance'
          }
        }
      ]
    }
  },
  computed: {
    localeMessages() {
      const locale = this.currentLanguage === 'ZH' ? 'zh-CN' : 'en'
      return P5_MESSAGES[locale] || P5_MESSAGES[P5_DEFAULT_LOCALE]
    },
    currentMessages() {
      return this.localeMessages
    },
    isChinese() {
      return this.currentLanguage === 'ZH'
    },
    localeClass() {
      return this.isChinese ? 'p5-page--zh-cn' : 'p5-page--en'
    },
    subnavBrand() {
      return this.isChinese ? 'ANOVATOR P5' : 'Anovator P5'
    },
    mobileUpgradeItems() {
      if (!this.isChinese) return this.upgradeItems

      return ['upper', 'lower', 'grip'].map((key) => this.upgradeItems.find((item) => item.key === key))
    },
    activeUpgrade() {
      return this.upgradeItems[this.activeUpgradeIndex]
    },
    activeUpgradeImage() {
      return this.getUpgradeImage(this.activeUpgrade)
    },
    activeService() {
      return this.serviceItems[this.activeServiceIndex]
    },
    activeServiceImage() {
      return this.getServiceImage(this.activeService)
    },
    selectedComparisonProduct() {
      return this.comparisonOptions.find(({ key }) => key === this.selectedComparisonKey) ||
        this.comparisonOptions[0]
    }
  },
  watch: {
    currentLanguage() {
      this.syncP5DocumentMetadata()
      this.$nextTick(() => {
        this.updateHeroScale()
        this.scheduleScrollUpdate()
      })
    }
  },
  created() {
    this.previousDocumentLang = document.documentElement.getAttribute('lang') || ''
    this.previousDocumentTitle = document.title
    this.syncP5DocumentMetadata()
  },
  mounted() {
    this.syncP5DocumentMetadata()
    document.documentElement.classList.add('p5-route-active')
    document.body.classList.add('p5-route-active')
    this.updateHeroScale()
    window.addEventListener('scroll', this.scheduleScrollUpdate, { passive: true })
    window.addEventListener('resize', this.scheduleScrollUpdate, { passive: true })
    window.addEventListener('resize', this.updateHeroScale, { passive: true })

    this.reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)')
    this.reducedMotion = this.reducedMotionQuery.matches
    this.syncHeroVideoPlayback()
    if (this.reducedMotionQuery.addEventListener) {
      this.reducedMotionQuery.addEventListener('change', this.updateReducedMotion)
    } else {
      this.reducedMotionQuery.addListener(this.updateReducedMotion)
    }

    if (this.upgrade4kMediaQuery) {
      if (this.upgrade4kMediaQuery.addEventListener) {
        this.upgrade4kMediaQuery.addEventListener('change', this.updateUpgrade4kViewport)
      } else {
        this.upgrade4kMediaQuery.addListener(this.updateUpgrade4kViewport)
      }
    }
    if (this.mobileLayoutMediaQuery) {
      if (this.mobileLayoutMediaQuery.addEventListener) {
        this.mobileLayoutMediaQuery.addEventListener('change', this.updateMobileLayout)
      } else {
        this.mobileLayoutMediaQuery.addListener(this.updateMobileLayout)
      }
    }

    if (this.$refs.upgradeSection) {
      this.$refs.upgradeSection.addEventListener('wheel', this.handleUpgradeWheel, { passive: false })
    }
    if (this.$refs.serviceSection) {
      this.$refs.serviceSection.addEventListener('wheel', this.handleServiceWheel, { passive: false })
    }
    document.addEventListener('mousedown', this.handleComparisonDocumentPointer)

    this.scheduleScrollUpdate()
  },
  beforeUnmount() {
    document.documentElement.classList.remove('p5-route-active')
    document.body.classList.remove('p5-route-active')
    window.removeEventListener('scroll', this.scheduleScrollUpdate)
    window.removeEventListener('resize', this.scheduleScrollUpdate)
    window.removeEventListener('resize', this.updateHeroScale)
    if (this.reducedMotionQuery) {
      if (this.reducedMotionQuery.removeEventListener) {
        this.reducedMotionQuery.removeEventListener('change', this.updateReducedMotion)
      } else {
        this.reducedMotionQuery.removeListener(this.updateReducedMotion)
      }
    }
    if (this.upgrade4kMediaQuery) {
      if (this.upgrade4kMediaQuery.removeEventListener) {
        this.upgrade4kMediaQuery.removeEventListener('change', this.updateUpgrade4kViewport)
      } else {
        this.upgrade4kMediaQuery.removeListener(this.updateUpgrade4kViewport)
      }
    }
    if (this.mobileLayoutMediaQuery) {
      if (this.mobileLayoutMediaQuery.removeEventListener) {
        this.mobileLayoutMediaQuery.removeEventListener('change', this.updateMobileLayout)
      } else {
        this.mobileLayoutMediaQuery.removeListener(this.updateMobileLayout)
      }
    }
    if (this.$refs.upgradeSection) {
      this.$refs.upgradeSection.removeEventListener('wheel', this.handleUpgradeWheel)
    }
    if (this.$refs.serviceSection) {
      this.$refs.serviceSection.removeEventListener('wheel', this.handleServiceWheel)
    }
    document.removeEventListener('mousedown', this.handleComparisonDocumentPointer)
    this.releaseUpgradeWheelLock()
    this.releaseServiceWheelLock()
    this.cancelUpgradePreload()
    this.cancelServicePreload()
    this.upgradePreloadImages = []
    this.servicePreloadImages = []
    if (this.scrollRafId !== null) {
      window.cancelAnimationFrame(this.scrollRafId)
      this.scrollRafId = null
    }
    window.clearTimeout(this.anchorScrollTimer)
    if (this.previousDocumentLang) {
      document.documentElement.setAttribute('lang', this.previousDocumentLang)
    } else {
      document.documentElement.removeAttribute('lang')
    }
    if (Object.values(P5_MESSAGES).some(({ meta }) => meta.title === document.title)) {
      document.title = this.previousDocumentTitle
    }
  },
  methods: {
    openContactModal(event) {
      requestContactModal(event?.currentTarget)
    },
    getP5Message(messages, key) {
      return key.split('.').reduce((value, segment) => {
        if (value === undefined || value === null) return undefined
        return value[segment]
      }, messages)
    },
    t(key) {
      const localizedValue = this.getP5Message(this.currentMessages, key)
      if (typeof localizedValue === 'string') return localizedValue

      const fallbackValue = this.getP5Message(P5_MESSAGES[P5_DEFAULT_LOCALE], key)
      if (typeof fallbackValue === 'string') {
        if (process.env.NODE_ENV !== 'production' && !this.missingP5TranslationWarnings.has(key)) {
          this.missingP5TranslationWarnings.add(key)
          const locale = this.currentLanguage === 'ZH' ? 'zh-CN' : 'en'
          console.warn(`[P5 i18n] Missing ${locale} translation for "${key}"; using English.`)
        }
        return fallbackValue
      }

      if (process.env.NODE_ENV !== 'production' && !this.missingP5TranslationWarnings.has(key)) {
        this.missingP5TranslationWarnings.add(key)
        console.warn(`[P5 i18n] Unknown translation key "${key}".`)
      }
      return ''
    },
    formatMessage(key, replacements) {
      return Object.entries(replacements).reduce(
        (message, [name, value]) => message.replaceAll(`{${name}}`, value),
        this.t(key)
      )
    },
    syncP5DocumentMetadata() {
      document.documentElement.setAttribute('lang', this.currentLanguage === 'ZH' ? 'zh-CN' : 'en')
      document.title = this.t('meta.title')
    },
    updateMobileLayout(event) {
      this.isMobileLayout = event.matches
      if (this.isMobileLayout) {
        this.releaseUpgradeWheelLock()
        this.releaseServiceWheelLock()
      }
      this.$nextTick(() => {
        this.updateHeroScale()
        this.syncHeroVideoPlayback()
        this.scheduleScrollUpdate()
      })
    },
    syncHeroVideoPlayback() {
      this.$nextTick(() => {
        const video = this.$refs.heroVideo
        if (!video) return

        video.muted = true
        if (this.reducedMotion) {
          video.pause()
          return
        }

        const playPromise = video.play()
        if (playPromise && typeof playPromise.catch === 'function') {
          playPromise.catch(() => {
            this.heroVideoPlaybackFailed = true
          })
        }
      })
    },
    handleHeroVideoPlaying() {
      this.heroVideoPlaybackFailed = false
    },
    handleHeroVideoError() {
      this.heroVideoPlaybackFailed = true
    },
    updateHeroScale() {
      if (typeof window === 'undefined') {
        return
      }

      if (window.innerWidth <= 768) {
        this.heroScale = 1
        this.heroCopyTop = '60px'
        return
      }

      const hero = this.$el.querySelector('.p5-hero')
      const heroWidth = hero
        ? hero.getBoundingClientRect().width
        : document.documentElement.clientWidth
      const nextScale = Math.max(0.1, heroWidth / 1440)
      this.heroCopyTop = `${60 / nextScale}px`

      if (Math.abs(nextScale - this.heroScale) > 0.0001) {
        this.heroScale = nextScale
      }
    },
    toggleComparisonMenu() {
      if (this.isComparisonMenuOpen) {
        this.closeComparisonMenu()
        return
      }

      this.openComparisonMenu(this.comparisonOptions.findIndex(({ key }) => key === this.selectedComparisonKey))
    },
    openComparisonMenu(index = 0) {
      const lastIndex = this.comparisonOptions.length - 1
      this.comparisonFocusIndex = Math.min(lastIndex, Math.max(0, index))
      this.isComparisonMenuOpen = true
      this.focusComparisonOption(this.comparisonFocusIndex)
    },
    closeComparisonMenu(restoreFocus = false) {
      this.isComparisonMenuOpen = false
      if (restoreFocus) {
        this.$nextTick(() => {
          this.$refs.comparisonMenuButton?.focus()
        })
      }
    },
    focusComparisonOption(index) {
      this.$nextTick(() => {
        const options = this.$refs.comparisonOptionButtons
        const option = Array.isArray(options) ? options[index] : options
        option?.focus()
      })
    },
    moveComparisonOptionFocus(index) {
      const lastIndex = this.comparisonOptions.length - 1
      this.comparisonFocusIndex = Math.min(lastIndex, Math.max(0, index))
      this.focusComparisonOption(this.comparisonFocusIndex)
    },
    selectComparisonProduct(key) {
      if (!this.comparisonOptions.some((product) => product.key === key)) return

      this.selectedComparisonKey = key
      this.comparisonFocusIndex = this.comparisonOptions.findIndex((product) => product.key === key)
      this.closeComparisonMenu(true)
    },
    handleComparisonTriggerKeydown(event) {
      const selectedIndex = this.comparisonOptions.findIndex(({ key }) => key === this.selectedComparisonKey)

      if (event.key === 'Escape' && this.isComparisonMenuOpen) {
        event.preventDefault()
        this.closeComparisonMenu(true)
        return
      }

      if (event.key === 'ArrowDown' || event.key === 'ArrowUp' || event.key === 'Home' || event.key === 'End') {
        event.preventDefault()
        if (event.key === 'Home') {
          this.openComparisonMenu(0)
        } else if (event.key === 'End') {
          this.openComparisonMenu(this.comparisonOptions.length - 1)
        } else {
          this.openComparisonMenu(event.key === 'ArrowDown'
            ? Math.min(this.comparisonOptions.length - 1, selectedIndex + 1)
            : Math.max(0, selectedIndex - 1))
        }
      }
    },
    handleComparisonOptionKeydown(event, index) {
      if (event.key === 'Escape') {
        event.preventDefault()
        this.closeComparisonMenu(true)
      } else if (event.key === 'ArrowDown') {
        event.preventDefault()
        this.moveComparisonOptionFocus(index === this.comparisonOptions.length - 1 ? 0 : index + 1)
      } else if (event.key === 'ArrowUp') {
        event.preventDefault()
        this.moveComparisonOptionFocus(index === 0 ? this.comparisonOptions.length - 1 : index - 1)
      } else if (event.key === 'Home') {
        event.preventDefault()
        this.moveComparisonOptionFocus(0)
      } else if (event.key === 'End') {
        event.preventDefault()
        this.moveComparisonOptionFocus(this.comparisonOptions.length - 1)
      } else if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault()
        this.selectComparisonProduct(this.comparisonOptions[index].key)
      } else if (event.key === 'Tab') {
        this.closeComparisonMenu()
      }
    },
    handleComparisonDocumentPointer(event) {
      if (!this.isComparisonMenuOpen || this.$refs.comparisonSelector?.contains(event.target)) return
      this.closeComparisonMenu()
    },
    getUpgradeImage(item) {
      if (!item || !this.isUpgradeViewportReady) return ''
      return this.is4kUpgradeViewport ? item.image4k : item.image
    },
    updateUpgrade4kViewport(event) {
      const matches = event.matches
      this.isUpgradeViewportReady = true
      if (this.is4kUpgradeViewport === matches) return

      this.cancelUpgradePreload()
      this.cancelServicePreload()
      this.releaseUpgradeWheelLock()
      this.releaseServiceWheelLock()
      this.upgradePreloadedSources.clear()
      this.servicePreloadedSources.clear()
      this.upgradePreloadImages = []
      this.servicePreloadImages = []
      this.is4kUpgradeViewport = matches
      this.$nextTick(this.scheduleScrollUpdate)
    },
    handleUpgradeImageLoad() {
      this.scheduleNextUpgradePreload()
    },
    scheduleNextUpgradePreload() {
      this.cancelUpgradePreload()
      const expectedIndex = this.activeUpgradeIndex
      if (expectedIndex >= this.upgradeItems.length - 1) return

      const preload = () => {
        this.upgradePreloadIdleId = null
        this.upgradePreloadTimer = null
        if (this.activeUpgradeIndex !== expectedIndex) return

        const source = this.getUpgradeImage(this.upgradeItems[expectedIndex + 1])
        if (!source || this.upgradePreloadedSources.has(source)) return

        const image = new Image()
        image.decoding = 'async'
        image.src = source
        this.upgradePreloadedSources.add(source)
        this.upgradePreloadImages.push(image)
      }

      if ('requestIdleCallback' in window) {
        this.upgradePreloadIdleId = window.requestIdleCallback(preload, { timeout: 1200 })
      } else {
        this.upgradePreloadTimer = window.setTimeout(preload, 250)
      }
    },
    cancelUpgradePreload() {
      if (this.upgradePreloadIdleId !== null && 'cancelIdleCallback' in window) {
        window.cancelIdleCallback(this.upgradePreloadIdleId)
      }
      window.clearTimeout(this.upgradePreloadTimer)
      this.upgradePreloadIdleId = null
      this.upgradePreloadTimer = null
    },
    getServiceImage(item) {
      if (!item || !this.isUpgradeViewportReady) return ''
      return this.is4kUpgradeViewport ? item.image4k : item.image
    },
    getServicePlatformImage(view) {
      if (!this.isUpgradeViewportReady) return ''
      const item = this.serviceItems.find(({ key }) => key === 'platform')
      if (!item) return ''

      if (view === 'mobile') {
        return this.is4kUpgradeViewport ? item.mobileImage4k : item.mobileImage
      }
      return this.is4kUpgradeViewport ? item.desktopImage4k : item.desktopImage
    },
    getServicePreloadSources(item) {
      if (!item) return []
      if (item.key === 'platform') {
        return [
          this.is4kUpgradeViewport ? item.desktopImage4k : item.desktopImage,
          this.is4kUpgradeViewport ? item.mobileImage4k : item.mobileImage
        ]
      }
      return [this.getServiceImage(item)]
    },
    handleServiceImageLoad() {
      this.scheduleNextServicePreload()
    },
    scheduleNextServicePreload() {
      this.cancelServicePreload()
      const expectedIndex = this.activeServiceIndex
      if (expectedIndex >= this.serviceItems.length - 1) return

      const preload = () => {
        this.servicePreloadIdleId = null
        this.servicePreloadTimer = null
        if (this.activeServiceIndex !== expectedIndex) return

        this.getServicePreloadSources(this.serviceItems[expectedIndex + 1]).forEach((source) => {
          if (!source || this.servicePreloadedSources.has(source)) return

          const image = new Image()
          image.decoding = 'async'
          image.src = source
          this.servicePreloadedSources.add(source)
          this.servicePreloadImages.push(image)
        })
      }

      if ('requestIdleCallback' in window) {
        this.servicePreloadIdleId = window.requestIdleCallback(preload, { timeout: 1200 })
      } else {
        this.servicePreloadTimer = window.setTimeout(preload, 250)
      }
    },
    cancelServicePreload() {
      if (this.servicePreloadIdleId !== null && 'cancelIdleCallback' in window) {
        window.cancelIdleCallback(this.servicePreloadIdleId)
      }
      window.clearTimeout(this.servicePreloadTimer)
      this.servicePreloadIdleId = null
      this.servicePreloadTimer = null
    },
    setActiveUpgrade(index, syncScroll = true) {
      if (index < 0 || index >= this.upgradeItems.length || index === this.activeUpgradeIndex) return false

      this.cancelUpgradePreload()
      this.activeUpgradeIndex = index
      if (syncScroll) {
        this.syncStoryScroll(this.$refs.upgradeSection, this.$refs.upgradeStage, index, this.upgradeItems.length)
      }
      return true
    },
    changeActiveUpgrade(direction) {
      return this.setActiveUpgrade(this.activeUpgradeIndex + direction)
    },
    isStorySticky(section, stage) {
      if (window.innerWidth <= 768 || !section || !stage || !section.contains(stage)) return false
      const track = stage.closest('.p5-story-track')
      const viewport = stage.closest('.p5-story-viewport')
      if (!track || !viewport || viewport.offsetHeight <= 0) return false

      const trackRect = track.getBoundingClientRect()
      const stickyOffset = this.getStoryStickyOffset()
      return trackRect.top <= stickyOffset + 1 &&
        trackRect.bottom >= stickyOffset + viewport.offsetHeight - 1
    },
    isUpgradeStorySticky() {
      return this.isStorySticky(this.$refs.upgradeSection, this.$refs.upgradeStage)
    },
    releaseUpgradeWheelLock() {
      window.clearTimeout(this.upgradeWheelUnlockTimer)
      this.upgradeWheelLocked = false
      this.upgradeWheelUnlockTimer = null
    },
    handleUpgradeWheel(event) {
      if (!this.isUpgradeStorySticky() || !Number.isFinite(event.deltaY) || event.deltaY === 0) return

      const direction = event.deltaY > 0 ? 1 : -1
      const isAtBoundary = (direction < 0 && this.activeUpgradeIndex === 0) ||
        (direction > 0 && this.activeUpgradeIndex === this.upgradeItems.length - 1)

      if (isAtBoundary) return

      event.preventDefault()
      if (this.upgradeWheelLocked) {
        window.clearTimeout(this.upgradeWheelUnlockTimer)
        this.upgradeWheelUnlockTimer = window.setTimeout(this.releaseUpgradeWheelLock, 220)
        return
      }

      if (!this.changeActiveUpgrade(direction)) return

      this.upgradeWheelLocked = true
      window.clearTimeout(this.upgradeWheelUnlockTimer)
      this.upgradeWheelUnlockTimer = window.setTimeout(this.releaseUpgradeWheelLock, 220)
    },
    handleUpgradeKeydown(event) {
      if (event.key !== 'ArrowUp' && event.key !== 'ArrowDown') return

      event.preventDefault()
      this.changeActiveUpgrade(event.key === 'ArrowUp' ? -1 : 1)
    },
    setActiveService(index, syncScroll = true) {
      if (index < 0 || index >= this.serviceItems.length || index === this.activeServiceIndex) return false

      this.cancelServicePreload()
      this.activeServiceIndex = index
      if (syncScroll) {
        this.syncStoryScroll(this.$refs.serviceSection, this.$refs.serviceStage, index, this.serviceItems.length)
      }
      return true
    },
    changeActiveService(direction) {
      return this.setActiveService(this.activeServiceIndex + direction)
    },
    isServiceStorySticky() {
      return this.isStorySticky(this.$refs.serviceSection, this.$refs.serviceStage)
    },
    releaseServiceWheelLock() {
      window.clearTimeout(this.serviceWheelUnlockTimer)
      this.serviceWheelLocked = false
      this.serviceWheelUnlockTimer = null
    },
    handleServiceWheel(event) {
      if (!this.isServiceStorySticky() || !Number.isFinite(event.deltaY) || event.deltaY === 0) return

      const direction = event.deltaY > 0 ? 1 : -1
      const isAtBoundary = (direction < 0 && this.activeServiceIndex === 0) ||
        (direction > 0 && this.activeServiceIndex === this.serviceItems.length - 1)

      if (isAtBoundary) return

      event.preventDefault()
      if (this.serviceWheelLocked) {
        window.clearTimeout(this.serviceWheelUnlockTimer)
        this.serviceWheelUnlockTimer = window.setTimeout(this.releaseServiceWheelLock, 220)
        return
      }

      if (!this.changeActiveService(direction)) return

      this.serviceWheelLocked = true
      this.serviceWheelUnlockTimer = window.setTimeout(this.releaseServiceWheelLock, 220)
    },
    handleServiceKeydown(event) {
      if (event.key !== 'ArrowUp' && event.key !== 'ArrowDown') return

      event.preventDefault()
      this.changeActiveService(event.key === 'ArrowUp' ? -1 : 1)
    },
    updateReducedMotion(event) {
      this.reducedMotion = event.matches
      this.syncHeroVideoPlayback()
    },
    scheduleScrollUpdate() {
      if (this.scrollRafId !== null) return

      this.scrollRafId = window.requestAnimationFrame(() => {
        this.scrollRafId = null
        this.updateActiveSection()
        this.updateStoryProgress()
      })
    },
    updateStoryProgress() {
      if (window.innerWidth <= 768) return

      this.updateStorySection(
        this.$refs.upgradeSection,
        this.$refs.upgradeStage,
        this.upgradeItems.length,
        'activeUpgradeIndex'
      )
      this.updateStorySection(
        this.$refs.serviceSection,
        this.$refs.serviceStage,
        this.serviceItems.length,
        'activeServiceIndex'
      )
    },
    updateStorySection(section, stage, itemCount, stateKey) {
      if (!section || !stage || !section.contains(stage) || itemCount < 2) return

      const track = stage.closest('.p5-story-track')
      if (!track) return
      const travel = track.offsetHeight - stage.offsetHeight
      if (travel <= 0) return

      const trackTop = track.getBoundingClientRect().top
      const scrolled = Math.min(travel, Math.max(0, this.getStoryStickyOffset() - trackTop))
      const step = travel / (itemCount - 1)
      const nextIndex = Math.min(itemCount - 1, Math.max(0, Math.round(scrolled / step)))

      if (this[stateKey] !== nextIndex) {
        this[stateKey] = nextIndex
      }
    },
    syncStoryScroll(section, stage, index, itemCount) {
      if (window.innerWidth <= 768 || !section || !stage || !section.contains(stage) || itemCount < 2) return

      const track = stage.closest('.p5-story-track')
      if (!track) return
      const travel = track.offsetHeight - stage.offsetHeight
      if (travel <= 0) return

      const step = travel / (itemCount - 1)
      const trackTop = track.getBoundingClientRect().top + window.scrollY
      const top = trackTop - this.getStoryStickyOffset() + (step * index)

      window.scrollTo({ top, behavior: 'auto' })
    },
    scrollToSection(id) {
      const target = document.getElementById(id)
      if (!target) return

      const isSectionLink = this.sectionLinks.some((item) => item.id === id)
      this.activeSection = isSectionLink ? id : ''
      this.anchorScrollSection = isSectionLink ? id : ''
      window.clearTimeout(this.anchorScrollTimer)
      if (this.anchorScrollSection) {
        this.anchorScrollTimer = window.setTimeout(() => {
          this.anchorScrollSection = ''
          this.scheduleScrollUpdate()
        }, 2500)
      }

      const top = target.getBoundingClientRect().top + window.scrollY - this.getStickyOffset()
      window.scrollTo({ top, behavior: this.reducedMotion ? 'auto' : 'smooth' })
    },
    getStoryStickyOffset() {
      const header = document.getElementById('header')
      const subnav = document.querySelector('.p5-subnav')
      const globalOffsetValue = window.getComputedStyle(document.documentElement)
        .getPropertyValue('--global-header-offset')
        .trim()
      const globalOffset = Number.parseFloat(globalOffsetValue)
      const headerOffset = globalOffsetValue && Number.isFinite(globalOffset)
        ? globalOffset
        : (header ? Math.max(0, header.getBoundingClientRect().bottom) : 44)

      return headerOffset + (subnav ? subnav.offsetHeight : 44)
    },
    getStickyOffset() {
      return this.getStoryStickyOffset() + 20
    },
    updateActiveSection() {
      const offset = this.getStickyOffset() + 24

      if (this.anchorScrollSection) {
        const anchorTarget = document.getElementById(this.anchorScrollSection)
        if (!anchorTarget || anchorTarget.getBoundingClientRect().top > offset) {
          this.activeSection = this.anchorScrollSection
          return
        }

        this.anchorScrollSection = ''
        window.clearTimeout(this.anchorScrollTimer)
      }

      let current = ''

      this.sectionLinks.forEach((item) => {
        const section = document.getElementById(item.id)
        if (section && section.getBoundingClientRect().top <= offset) {
          current = item.id
        }
      })

      this.activeSection = current
    }
  }
}
</script>

<style scoped>
:global(html.p5-route-active),
:global(body.p5-route-active) {
  overflow-x: clip;
  overflow-y: visible;
}

.p5-page {
  --p5-accent: #ef0a6a;
  --p5-orange: linear-gradient(171deg, #ff6e00 0%, #e2a56f 102%);
  --p5-bg: #000;
  --p5-section-bg: #191b1d;
  --p5-card-bg: #2d2d2d;
  --p5-muted: rgba(255, 255, 255, 0.6);
  --p5-max: 1440px;
  --p5-content: 1296px;
  --p5-fixed-page-width: 1440px;
  --p5-fixed-content-width: 1296px;
  --p5-gutter: clamp(24px, 3.75vw, 72px);
  --p5-radius: 24px;
  --p5-subnav-height: 44px;
  --p5-story-stage-height: min(
    770px,
    calc(100vh - var(--global-header-offset, 44px) - var(--p5-subnav-height))
  );
  --p5-story-step: 480px;
  --p5-story-sticky-top: calc(var(--global-header-offset, 44px) + var(--p5-subnav-height));
  min-width: 0;
  padding-top: var(--global-header-height, 64px);
  overflow: clip;
  background: var(--p5-bg);
  color: #fff;
  font-family: 'MiSans-Regular', 'Segoe UI', Arial, sans-serif;
}

.p5-page *,
.p5-page *::before,
.p5-page *::after {
  box-sizing: border-box;
}

.p5-page h1,
.p5-page h2,
.p5-page h3,
.p5-page p,
.p5-page ul,
.p5-page dl,
.p5-page dd {
  margin: 0;
}

.p5-page h1,
.p5-page h2,
.p5-page h3 {
  color: #fff !important;
}

.p5-page p {
  font-size: inherit !important;
}

.p5-page ul {
  padding: 0;
  list-style: none;
}

.p5-page img {
  display: block;
  max-width: 100%;
}

.p5-subnav {
  position: sticky;
  top: var(--global-header-offset);
  z-index: 90;
  height: var(--p5-subnav-height);
  background: #191b1d;
}

.p5-subnav__inner {
  width: min(100%, var(--p5-content));
  height: 100%;
  margin: 0 auto;
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) auto;
  align-items: center;
  column-gap: 48px;
}

.p5-subnav a {
  color: #cfcfcf;
  font-size: 12px;
  font-weight: 400;
  line-height: normal;
  text-decoration: none;
  white-space: nowrap;
  transition: color 0.2s ease;
}

.p5-subnav__brand {
  flex: 0 0 auto;
  color: #fff !important;
}

.p5-subnav__links {
  min-width: 0;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: clamp(38px, 2.5vw, 48px);
  white-space: nowrap;
}

.p5-subnav__actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
  white-space: nowrap;
}

.p5-subnav__links > a:not(.p5-subnav__cta) {
  color: #cfcfcf;
}

.p5-subnav__links > a:not(.p5-subnav__cta):hover,
.p5-subnav__links > a:not(.p5-subnav__cta):focus-visible {
  color: #fff;
}

.p5-subnav__links > a.active,
.p5-subnav__links > a.active:hover,
.p5-subnav__links > a.active:focus-visible {
  color: #ef0a6a;
}

.p5-subnav__cta {
  flex: 0 0 auto;
  min-width: 98px;
  height: 25px;
  padding: 0 12px;
  border: 0;
  border-radius: 15px;
  background: #fff;
  color: #000 !important;
  font-size: 11px !important;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  text-align: center;
}

.p5-subnav a:focus-visible,
.p5-subscribe__field button:focus-visible {
  outline: 2px solid #fff;
  outline-offset: 3px;
}

.p5-hero {
  position: relative;
  height: clamp(760px, 53.47vw, 1027px);
  overflow: hidden;
  background: #0d1319;
}

.p5-hero__canvas {
  display: contents;
}

.p5-hero__poster,
.p5-hero__video {
  position: absolute;
  z-index: 1;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center center;
  pointer-events: none;
}

.p5-hero__video {
  opacity: 1;
}

.p5-hero__video--hidden {
  opacity: 0;
}

.p5-hero__video::-webkit-media-controls,
.p5-hero__video::-webkit-media-controls-enclosure {
  display: none !important;
}

.p5-hero__background {
  position: absolute;
  right: 0;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 81.8%;
  object-fit: cover;
}

.p5-hero__copy {
  position: relative;
  z-index: 2;
  padding-top: clamp(78px, 5vw, 96px);
  text-align: center;
}

.p5-hero__copy p {
  color: var(--p5-muted);
  -webkit-text-fill-color: var(--p5-muted);
  font-size: clamp(16px, 1.11vw, 21px);
  line-height: 1.3;
}

.p5-hero__copy h1 {
  margin-top: 10px;
  opacity: 1;
  visibility: visible;
  -webkit-text-fill-color: #fff;
  font-family: 'MiSans-Heavy', 'Segoe UI', sans-serif;
  font-size: clamp(42px, 2.78vw, 54px);
  line-height: 1.15;
  text-transform: uppercase;
}

.p5-hero__product {
  position: absolute;
  z-index: 2;
  bottom: 13.3%;
  left: 50%;
  width: clamp(420px, 29vw, 557px);
  transform: translateX(-50%);
}

@media (min-width: 769px) {
  .p5-hero {
    width: 100%;
    height: calc(770px * var(--p5-hero-scale, 1));
    margin: 0;
  }

  .p5-hero__canvas {
    position: absolute;
    top: 0;
    left: 50%;
    display: block;
    width: 1440px;
    height: 770px;
    margin-left: -720px;
    transform: scale(var(--p5-hero-scale, 1));
    transform-origin: top center;
  }

  .p5-hero__background {
    width: 1440px;
    height: 630px;
    object-fit: fill;
  }

  .p5-hero__copy {
    position: absolute;
    inset: 0;
    padding: 0;
  }

  .p5-hero__copy p {
    position: absolute;
    top: var(--p5-hero-copy-top, 60px);
    left: 0;
    width: 100%;
    font-size: 16px !important;
    line-height: normal;
  }

  .p5-hero__copy h1 {
    position: absolute;
    top: calc(var(--p5-hero-copy-top, 60px) + 29px);
    left: 0;
    width: 100%;
    margin: 0;
    font-size: 40px;
    line-height: normal;
  }

  .p5-hero__product {
    top: 297px;
    bottom: auto;
    left: 513px;
    width: 418px;
    height: 370px;
    object-fit: contain;
    transform: none;
  }
}

.p5-section {
  scroll-margin-top: 148px;
}

.p5-fixed-section {
  width: 100%;
}

.p5-fixed-section__inner {
  width: min(100%, var(--p5-fixed-page-width));
  margin-right: auto;
  margin-left: auto;
}

.p5-fixed-section__content {
  width: min(calc(100% - (2 * var(--p5-gutter))), var(--p5-fixed-content-width));
  margin-right: auto;
  margin-left: auto;
}

.p5-section-heading {
  height: 394px;
  padding: 120px var(--p5-gutter) 0;
  text-align: center;
}

.p5-section-heading > p,
.p5-kicker {
  display: inline-block;
  background: var(--p5-orange);
  background-clip: text;
  color: transparent;
  font-family: 'MiSans-Bold', 'Segoe UI', sans-serif;
}

.p5-section-heading > p {
  font-size: clamp(20px, 1.25vw, 24px);
  line-height: 1.35;
}

.p5-section-heading h2 {
  width: min(100%, 746px);
  margin: 8px auto 0;
  font-family: 'MiSans-Bold', 'Segoe UI', sans-serif;
  font-size: clamp(34px, 2.08vw, 40px);
  line-height: 1.32;
}

.p5-upgrade {
  background: #000;
}

.p5-upgrade > .p5-section-heading,
.p5-services > .p5-section-heading {
  background: #000;
}

.p5-upgrade > .p5-section-heading {
  height: 341px;
}

.p5-services > .p5-section-heading {
  height: 394px;
}

.p5-story-track {
  position: relative;
  height: calc(var(--p5-story-stage-height) + (2 * var(--p5-story-step)));
  background: #000;
}

.p5-story-viewport {
  position: sticky;
  top: var(--p5-story-sticky-top);
  height: var(--p5-story-stage-height);
}

.p5-upgrade-stage {
  width: min(100%, var(--p5-max));
  height: var(--p5-story-stage-height);
  margin: 0 auto;
  display: grid;
  grid-template-columns: 127px 361px minmax(0, 1fr);
  overflow: hidden;
  background: #000;
}

.p5-upgrade-accordion {
  height: 100%;
  padding: 0 31px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  border-right: 1px solid rgba(255, 255, 255, 0.12);
}

.p5-upgrade-accordion__items {
  width: 299px;
  flex: 0 1 auto;
  min-height: 0;
}

.p5-upgrade-accordion__item {
  border-bottom: 1px solid rgba(255, 255, 255, 0.16);
}

.p5-upgrade-accordion__item:first-child {
  border-top: 1px solid rgba(255, 255, 255, 0.16);
}

.p5-upgrade-accordion__trigger {
  width: 100%;
  min-height: 88px;
  padding: 20px 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
  border: 0;
  background: transparent;
  color: rgba(255, 255, 255, 0.62);
  font: inherit;
  font-size: 18px;
  line-height: 1.35;
  text-align: left;
  cursor: pointer;
  transition: color 180ms ease;
}

.p5-upgrade-accordion__item.active .p5-upgrade-accordion__trigger {
  color: #fff;
}

.p5-upgrade-accordion__trigger:hover {
  color: #fff;
}

.p5-upgrade-controls button:focus-visible {
  outline: 2px solid var(--p5-accent);
  outline-offset: 4px;
}

.p5-upgrade-accordion__trigger:focus,
.p5-upgrade-accordion__trigger:focus-visible,
.p5-upgrade-accordion__trigger:active {
  outline: none;
  box-shadow: none;
}

.p5-upgrade-accordion__symbol {
  flex: 0 0 28px;
  color: var(--p5-accent);
  font-size: 28px;
  line-height: 1;
  text-align: center;
}

.p5-upgrade-accordion__panel {
  padding: 2px 0 28px;
}

.p5-upgrade-accordion__headlines {
  display: grid;
  gap: 6px;
}

.p5-upgrade-accordion__panel h3 {
  font-family: 'MiSans-Bold', 'Segoe UI', sans-serif;
  font-size: 24px;
  line-height: 1.4;
}

.p5-upgrade-accordion__panel ul {
  margin-top: 16px;
  padding-left: 20px;
  list-style: disc;
  color: var(--p5-muted);
  font-size: 18px;
  line-height: 1.45;
}

.p5-upgrade-accordion__panel li + li {
  margin-top: 6px;
}

.p5-page .p5-upgrade-accordion__mobile-image {
  display: none;
}

.p5-upgrade-controls {
  padding-left: 72px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  gap: 18px;
  border-right: 1px solid rgba(255, 255, 255, 0.12);
  background: #000;
}

.p5-upgrade-controls button {
  width: 40px;
  height: 40px;
  border: 1px solid rgba(255, 255, 255, 0.24);
  border-radius: 50%;
  background: transparent;
  color: #fff;
  font-size: 20px;
  cursor: pointer;
  transition: border-color 180ms ease, background 180ms ease;
}

.p5-upgrade-controls button:hover:not(:disabled) {
  border-color: var(--p5-accent);
  background: rgba(239, 10, 106, 0.1);
}

.p5-upgrade-controls button:disabled {
  opacity: 0.28;
  cursor: not-allowed;
}

.p5-upgrade-visual {
  position: relative;
  min-width: 0;
  height: 100%;
  overflow: hidden;
  background: #000;
}

.p5-upgrade-visual img {
  position: absolute;
  pointer-events: none;
}

.p5-upgrade-image--upper {
  top: 188px;
  left: 24px;
  width: 856px;
  height: 394px;
  object-fit: cover;
}

.p5-upgrade-image--lower {
  top: 0;
  left: 132px;
  width: 748px;
  height: 770px;
  object-fit: contain;
  object-position: center bottom;
}

.p5-upgrade-image--grip {
  top: 66px;
  left: 53px;
  width: 827px;
  height: 637px;
  object-fit: contain;
}

.p5-upgrade-accordion__panel--motion {
  animation: p5-upgrade-content-in 220ms ease both;
}

.p5-upgrade-visual__image--motion {
  animation: p5-upgrade-image-in 220ms ease both;
}

@keyframes p5-upgrade-content-in {
  from {
    opacity: 0;
    transform: translateY(8px);
  }
}

@keyframes p5-upgrade-image-in {
  from {
    opacity: 0;
    transform: translateX(14px);
  }
}

.p5-features {
  min-height: 1684px;
  background: var(--p5-section-bg);
}

.p5-feature-grid {
  min-height: 1170px;
  margin-bottom: 120px;
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  grid-template-rows: repeat(3, 374px);
  grid-template-areas:
    'bia posture agility'
    'bia posture balance'
    'blood vital vital';
  gap: 24px;
}

.p5-feature-card {
  position: relative;
  min-width: 0;
  overflow: hidden;
  border-radius: var(--p5-radius);
  background: var(--p5-card-bg);
}

.p5-feature-card h3 {
  position: absolute;
  right: 31px;
  bottom: 31px;
  left: 31px;
  z-index: 2;
  font-family: 'MiSans-Regular', 'Segoe UI', sans-serif;
  font-size: clamp(19px, 1.25vw, 24px);
  line-height: 1.34;
}

.p5-feature-card > img {
  position: absolute;
}

.p5-feature-card--bia {
  grid-area: bia;
}

.p5-feature-card--bia img {
  top: 29px;
  left: 50%;
  width: 75.7%;
  transform: translateX(-50%);
}

.p5-feature-card--posture {
  grid-area: posture;
  background: linear-gradient(152deg, #939393 0%, #2e3636 99%);
}

.p5-feature-card--posture img {
  top: 17px;
  left: 0;
  width: 100%;
  height: 78%;
  object-fit: contain;
  object-position: left top;
}

.p5-feature-card--posture .p5-feature-card__copy {
  position: absolute;
  right: 31px;
  bottom: 31px;
  left: 31px;
  z-index: 2;
}

.p5-feature-card--posture .p5-feature-card__copy p {
  margin-bottom: 5px;
  color: rgba(255, 255, 255, 0.68);
  font-size: clamp(14px, 0.94vw, 18px) !important;
  line-height: 1.3;
}

.p5-feature-card--posture .p5-feature-card__copy h3 {
  position: static;
}

.p5-feature-card--agility {
  grid-area: agility;
}

.p5-agility-visual {
  position: absolute;
  top: 41px;
  left: 50%;
  width: 290px;
  display: grid;
  grid-template-columns: repeat(3, 90px);
  gap: 10px;
  transform: translateX(-50%);
}

.p5-agility-visual img {
  display: block;
  width: 90px;
  height: 70px;
  object-fit: contain;
}

.p5-feature-card--balance {
  grid-area: balance;
}

.p5-feature-card--balance img {
  top: 0;
  left: 0;
  width: 100%;
  height: 94%;
  object-fit: cover;
  object-position: center;
}

.p5-feature-card--blood {
  grid-area: blood;
}

.p5-feature-card--blood img {
  top: 59px;
  left: 50%;
  width: 56.7%;
  transform: translateX(-50%);
}

.p5-feature-card--vital {
  grid-area: vital;
}

.p5-feature-card--vital img {
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center;
}

.p5-design {
  width: 100%;
  background: #000;
}

.p5-design-card {
  position: relative;
  overflow: hidden;
  background: #000;
}

.p5-design-card__copy {
  position: relative;
  z-index: 2;
  width: min(100% - (2 * var(--p5-gutter)), 900px);
  margin: 0 auto;
  text-align: center;
}

.p5-design-card h3 {
  font-family: 'MiSans-Bold', 'Segoe UI', sans-serif;
  font-size: clamp(40px, 3.75vw, 54px);
  line-height: 1.34;
  text-transform: uppercase;
}

.p5-design-card h3 span {
  background: var(--p5-orange);
  background-clip: text;
  color: transparent;
}

.p5-page--zh-cn .p5-design-card--screen h3,
.p5-page--en .p5-design-card--screen h3 {
  font-weight: 630;
  font-synthesis: none;
}

.p5-page--zh-cn .p5-design-card--screen .p5-screen-title__highlight {
  background-image: linear-gradient(171.54286866454225deg, #ff6e00 0%, #e2a56f 102.11%);
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  color: transparent;
}

.p5-page--en .p5-design-card--screen .p5-screen-title__highlight {
  background-image: linear-gradient(175.09108146490112deg, #ff6e00 0%, #e2a56f 102.11%);
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  color: transparent;
}

.p5-design-card__copy p {
  width: min(100%, 746px);
  margin: 16px auto 0;
  color: var(--p5-muted);
  font-size: 18px;
  line-height: 1.34;
}

.p5-design-card > img {
  position: absolute;
  pointer-events: none;
}

.p5-design-card--chassis {
  height: 1113px;
  padding-top: 0;
}

.p5-design-card--chassis img {
  bottom: 0;
  left: 50%;
  width: 470px;
  height: 873px;
  object-fit: cover;
  transform: translateX(-50%);
}

.p5-design-card--screen {
  height: 1116px;
  padding-top: 120px;
}

.p5-design-card--screen img {
  bottom: 0;
  left: 50%;
  width: 478px;
  height: 756px;
  transform: translateX(-50%);
}

.p5-design-card--turntable {
  height: 794px;
  padding-top: 120px;
}

.p5-design-card--turntable img {
  right: 72px;
  bottom: 0;
  width: min(78%, 1021px);
}

.p5-design-card--contours {
  height: 760px;
  padding-top: 120px;
}

.p5-design-card--contours img {
  bottom: 0;
  left: 0;
  width: min(76%, 1004px);
}

.p5-services {
  background: #000;
}

.p5-service-stage {
  width: min(100%, var(--p5-max));
  height: var(--p5-story-stage-height);
  margin: 0 auto;
  display: grid;
  grid-template-columns: 127px 361px minmax(0, 1fr);
  overflow: hidden;
  background: #000;
}

.p5-service-controls {
  padding-left: 72px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  gap: 18px;
  border-right: 1px solid rgba(255, 255, 255, 0.12);
  background: #000;
}

.p5-service-controls button {
  width: 40px;
  height: 40px;
  border: 1px solid rgba(255, 255, 255, 0.24);
  border-radius: 50%;
  background: transparent;
  color: #fff;
  font-size: 20px;
  cursor: pointer;
  transition: border-color 180ms ease, background 180ms ease;
}

.p5-service-controls button:hover:not(:disabled) {
  border-color: var(--p5-accent);
  background: rgba(239, 10, 106, 0.1);
}

.p5-service-controls button:disabled {
  opacity: 0.28;
  cursor: not-allowed;
}

.p5-service-controls button:focus-visible {
  outline: 2px solid var(--p5-accent);
  outline-offset: 4px;
}

.p5-service-accordion__trigger:focus,
.p5-service-accordion__trigger:focus-visible,
.p5-service-accordion__trigger:active {
  outline: none;
  box-shadow: none;
}

.p5-service-accordion {
  height: 100%;
  padding: 0 31px;
  display: flex;
  align-items: center;
  border-right: 1px solid rgba(255, 255, 255, 0.12);
}

.p5-service-accordion__items {
  width: 299px;
  min-width: 0;
}

.p5-service-accordion__item {
  border-bottom: 1px solid rgba(255, 255, 255, 0.16);
}

.p5-service-accordion__item:first-child {
  border-top: 1px solid rgba(255, 255, 255, 0.16);
}

.p5-service-accordion__trigger {
  width: 100%;
  min-height: 88px;
  padding: 20px 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
  border: 0;
  background: transparent;
  color: rgba(255, 255, 255, 0.56);
  font-family: 'MiSans-Bold', 'Segoe UI', sans-serif;
  font-size: 18px;
  line-height: 1.35;
  text-align: left;
  cursor: pointer;
  transition: color 180ms ease;
}

.p5-service-accordion__item.active .p5-service-accordion__trigger,
.p5-service-accordion__trigger:hover {
  color: #fff;
}

.p5-service-accordion__symbol {
  flex: 0 0 24px;
  font-size: 24px;
  line-height: 1;
  text-align: center;
}

.p5-service-accordion__panel {
  padding: 2px 0 28px;
}

.p5-service-accordion__panel ul {
  padding-left: 20px;
  list-style: disc;
  color: var(--p5-muted);
  font-size: 18px;
  line-height: 1.45;
}

.p5-service-accordion__panel li + li {
  margin-top: 6px;
}

.p5-service-accordion__mobile-visual {
  display: none;
}

.p5-service-visual {
  position: relative;
  min-width: 0;
  height: 100%;
  overflow: hidden;
  background: #000;
}

.p5-service-visual__content {
  position: absolute;
  inset: 0;
}

.p5-service-visual__image {
  object-fit: contain;
}

.p5-service-visual > .p5-service-visual__content .p5-service-visual__image {
  position: absolute;
}

.p5-service-visual > .p5-service-visual--reports .p5-service-visual__image {
  top: 111px;
  right: 0;
  width: 677px;
  height: 547px;
}

.p5-service-visual > .p5-service-visual--trends .p5-service-visual__image {
  top: 140px;
  right: 72px;
  width: 605px;
  height: 489px;
}

.p5-service-visual__platform {
  position: relative;
  width: 550px;
  aspect-ratio: 550 / 437;
}

.p5-service-visual > .p5-service-visual__content .p5-service-visual__platform {
  position: absolute;
  top: 166px;
  right: 103px;
}

.p5-service-visual__platform img:first-child {
  width: calc(100% - 22px);
  height: 100%;
  margin-left: 22px;
  object-fit: contain;
}

.p5-service-visual__platform img:last-child {
  position: absolute;
  bottom: 0;
  left: 0;
  width: 16%;
  height: auto;
}

.p5-service-accordion__panel--motion,
.p5-service-visual__content--motion {
  animation: p5-service-content-in 220ms ease both;
}

@keyframes p5-service-content-in {
  from {
    opacity: 0;
    transform: translateY(8px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.p5-comparison {
  background: #000;
}

.p5-comparison__heading {
  height: 253px;
  padding-top: 120px;
  text-align: center;
}

.p5-comparison__heading h2 {
  width: min(100% - (2 * var(--p5-gutter)), 746px);
  margin: 0 auto;
  font-family: 'MiSans-Bold', 'Segoe UI', sans-serif;
  font-size: clamp(34px, 2.08vw, 40px);
  line-height: 1.33;
}

.p5-comparison__panel {
  min-height: 1491px;
  padding: 0 clamp(48px, 11.46vw, 220px) 120px;
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 416px));
  justify-content: space-between;
  gap: 24px;
  overflow: visible;
  border-radius: 32px;
  background: #222;
}

.p5-product-card {
  min-width: 0;
  text-align: center;
}

.p5-product-card > img {
  width: 100%;
  aspect-ratio: 1;
  object-fit: cover;
}

.p5-product-card__model {
  position: relative;
  min-height: 53px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.p5-product-card h3 {
  margin: 0;
  font-family: 'MiSans-Bold', 'Segoe UI', sans-serif;
  font-size: 40px;
  line-height: 1.33;
}

.p5-product-card__functions {
  height: 352px;
  min-height: 352px;
  margin-top: 8px !important;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  color: rgba(255, 255, 255, 0.5);
  font-size: 16px;
  line-height: 1.3;
}

.p5-product-card__functions li {
  width: 100%;
  min-height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.p5-product-card__functions .is-unsupported {
  color: rgba(255, 255, 255, 0.32);
}

.p5-comparison-selector__trigger {
  width: 34px;
  height: 34px;
  margin-left: 12px;
  padding: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 34px;
  border: 1px solid rgba(255, 255, 255, 0.34);
  border-radius: 50%;
  background: transparent;
  color: #fff;
  cursor: pointer;
}

.p5-comparison-selector__trigger span {
  width: 8px;
  height: 8px;
  border-right: 1.5px solid currentColor;
  border-bottom: 1.5px solid currentColor;
  transform: translateY(-2px) rotate(45deg);
}

.p5-comparison-selector__trigger:hover,
.p5-comparison-selector__trigger:focus-visible {
  border-color: #fff;
  background: rgba(255, 255, 255, 0.08);
}

.p5-comparison-selector__trigger:focus-visible {
  outline: 2px solid var(--p5-pink);
  outline-offset: 3px;
}

.p5-comparison-selector__menu {
  position: absolute;
  top: calc(100% + 8px);
  left: 50%;
  z-index: 20;
  width: 148px;
  padding: 6px;
  display: grid;
  gap: 2px;
  border: 1px solid rgba(255, 255, 255, 0.18);
  border-radius: 12px;
  background: #191b1d;
  box-shadow: 0 12px 28px rgba(0, 0, 0, 0.42);
  transform: translateX(-50%);
}

.p5-comparison-selector__option {
  width: 100%;
  min-height: 38px;
  padding: 7px 14px;
  border: 0;
  border-radius: 8px;
  background: transparent;
  color: #cfcfcf;
  font: inherit;
  text-align: left;
  cursor: pointer;
}

.p5-comparison-selector__option:focus,
.p5-comparison-selector__option:focus-visible,
.p5-comparison-selector__option:active {
  border-color: transparent;
  outline: none;
  box-shadow: none;
}

.p5-comparison-selector__option:hover,
.p5-comparison-selector__option:focus-visible {
  background: rgba(255, 255, 255, 0.08);
  color: #fff;
}

.p5-comparison-selector__option.is-selected,
.p5-comparison-selector__option[aria-selected='true'],
.p5-comparison-selector__option.is-selected:hover,
.p5-comparison-selector__option.is-selected:focus,
.p5-comparison-selector__option.is-selected:focus-visible,
.p5-comparison-selector__option.is-selected:active {
  border-color: transparent;
  background: #2c2e30;
  color: var(--p5-pink);
  outline: none;
  box-shadow: none;
}

.p5-product-card dl > div {
  min-height: 154px;
  padding: 32px 12px 0;
  border-top: 1px solid rgba(255, 255, 255, 0.22);
}

.p5-product-card dt {
  color: #fff;
  font-family: 'MiSans-Bold', 'Segoe UI', sans-serif;
  font-size: 28px;
  font-weight: 700;
  line-height: 1.34;
}

.p5-product-card dd {
  max-width: 100%;
  margin: 14px 0 0;
  color: rgba(255, 255, 255, 0.5);
  font-family: 'MiSans-Regular', 'Segoe UI', sans-serif;
  font-size: 16px;
  font-weight: 400;
  line-height: 1.5;
  overflow-wrap: anywhere;
}

.p5-subscribe-section,
.p5-disclaimer-section {
  background: #000;
}

.p5-subscribe {
  height: 428px;
  padding-top: 120px;
  text-align: center;
}

.p5-subscribe h2 {
  font-family: 'MiSans-Bold', 'Segoe UI', sans-serif;
  font-size: 40px;
  line-height: 1.33;
}

.p5-subscribe > p {
  margin-top: 8px;
  color: var(--p5-muted);
  font-size: 18px;
}

.p5-subscribe__field {
  width: min(calc(100% - (2 * var(--p5-gutter))), 526px);
  height: 54px;
  margin: 44px auto 0;
  padding: 0 31px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-radius: 65px;
  background: #fff;
  color: rgba(0, 0, 0, 0.4);
  font-size: 16px;
  text-align: left;
}

.p5-subscribe__field button {
  padding: 0;
  border: 0;
  background: transparent;
  color: rgba(0, 0, 0, 0.6);
  font-size: 18px;
}

.p5-disclaimer {
  min-height: 156px;
  margin-bottom: 80px;
  padding-top: 28px;
  border-top: 1px solid rgba(255, 255, 255, 0.35);
  color: var(--p5-muted);
  font-size: 12px;
  line-height: 1.33;
}

.p5-disclaimer h2 {
  margin: 0 0 12px;
  color: inherit;
  font-family: 'MiSans-Regular', 'Segoe UI', sans-serif;
  font-size: 12px;
  font-weight: 400;
}

.p5-disclaimer p + p {
  margin-top: 4px;
}

.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

@media (max-width: 1100px) {
  .p5-page {
    --p5-gutter: 40px;
  }

  .p5-subnav__inner {
    padding: 0 var(--p5-gutter);
    column-gap: 24px;
  }

  .p5-subnav__links {
    gap: clamp(18px, 2vw, 28px);
  }

  .p5-upgrade-stage {
    grid-template-columns: 60px minmax(360px, 38%) minmax(0, 1fr);
  }

  .p5-upgrade-controls {
    padding-left: 18px;
  }

  .p5-upgrade-controls button {
    width: 40px;
    height: 40px;
  }

  .p5-service-stage {
    grid-template-columns: 60px minmax(360px, 38%) minmax(0, 1fr);
  }

  .p5-service-controls {
    padding-left: 18px;
  }

  .p5-service-controls button {
    width: 40px;
    height: 40px;
  }

  .p5-feature-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    grid-template-rows: repeat(3, 540px);
    grid-template-areas:
      'bia posture'
      'agility balance'
      'blood vital';
  }

  .p5-feature-card--vital img {
    object-position: center;
  }

  .p5-comparison__panel {
    padding-right: 56px;
    padding-left: 56px;
  }
}

@media (min-width: 769px) and (max-width: 1100px) {
  .p5-upgrade-accordion {
    padding: 34px 30px 26px;
  }

  .p5-upgrade-accordion__trigger {
    min-height: 76px;
    padding: 14px 0;
  }

  .p5-upgrade-accordion__headlines {
    gap: 4px;
  }

  .p5-upgrade-accordion__panel h3 {
    line-height: 1.32;
  }

  .p5-upgrade-accordion__panel ul {
    margin-top: 11px;
    line-height: 1.4;
  }

  .p5-upgrade-accordion__panel li + li {
    margin-top: 5px;
  }
}

@media (min-width: 3000px) and (min-height: 1600px) {
  .p5-upgrade,
  .p5-services {
    --p5-story-stage-height: 2053.333px;
    --p5-story-step: 720px;
  }

  .p5-upgrade .p5-story-track,
  .p5-services .p5-story-track {
    height: calc(var(--p5-story-stage-height) + (2 * var(--p5-story-step)));
  }

  .p5-upgrade .p5-story-viewport,
  .p5-services .p5-story-viewport {
    position: sticky;
    top: var(--p5-story-sticky-top);
    height: 2032.8px;
    overflow: hidden;
  }

  .p5-upgrade-stage,
  .p5-service-stage {
    position: static;
    top: auto;
    width: 3840px;
    max-width: none;
    height: 2053.333px;
    grid-template-columns: 338.667px 962.666px minmax(0, 1fr);
    transform: scale(0.99);
    transform-origin: top center;
  }

  .p5-upgrade-controls,
  .p5-service-controls {
    padding-left: 192px;
    gap: 53.333px;
  }

  .p5-upgrade-controls button,
  .p5-service-controls button {
    width: 106.667px;
    height: 106.667px;
    font-size: 48px;
  }

  .p5-upgrade-accordion,
  .p5-service-accordion {
    padding: 0 82.667px;
  }

  .p5-upgrade-accordion__items,
  .p5-service-accordion__items {
    width: 797.333px;
  }

  .p5-upgrade-accordion__trigger,
  .p5-service-accordion__trigger {
    min-height: 234.667px;
    padding: 53.333px 0;
    gap: 53.333px;
    font-size: 48px;
  }

  .p5-upgrade-accordion__symbol,
  .p5-service-accordion__symbol {
    flex-basis: 32px;
    font-size: 32px;
  }

  .p5-upgrade-accordion__panel,
  .p5-service-accordion__panel {
    padding: 5.333px 0 74.667px;
  }

  .p5-upgrade-accordion__headlines {
    gap: 16px;
  }

  .p5-upgrade-accordion__panel h3 {
    font-size: 64px;
  }

  .p5-upgrade-accordion__panel ul {
    margin-top: 42.667px;
    padding-left: 53.333px;
    font-size: 48px;
  }

  .p5-upgrade-accordion__panel li + li,
  .p5-service-accordion__panel li + li {
    margin-top: 16px;
  }

  .p5-service-accordion__panel ul {
    padding-left: 53.333px;
    font-size: 48px;
  }

  .p5-upgrade-visual .p5-upgrade-image--upper {
    top: 501.333px;
    left: 64px;
    width: 2282.667px;
    height: 1050.667px;
    object-fit: fill;
  }

  .p5-upgrade-visual .p5-upgrade-image--lower {
    top: 0;
    left: 352px;
    width: 1994.667px;
    height: 2053.333px;
    object-fit: fill;
  }

  .p5-upgrade-visual .p5-upgrade-image--grip {
    top: 177.333px;
    left: 141.333px;
    width: 2205.333px;
    height: 1698.667px;
    object-fit: fill;
  }

  .p5-service-visual > .p5-service-visual--reports .p5-service-visual__image {
    top: 296.667px;
    right: 0;
    width: 1805.333px;
    height: 1459.187px;
  }

  .p5-service-visual > .p5-service-visual--trends .p5-service-visual__image {
    top: 374.667px;
    right: 192px;
    width: 1613.333px;
    height: 1304px;
  }

  .p5-service-visual > .p5-service-visual__content .p5-service-visual__platform {
    top: 445.333px;
    right: 274.667px;
    width: 1466.667px;
    height: 1165.333px;
  }

  .p5-service-visual__platform img:first-child {
    width: 1408px;
    height: 1165.333px;
    margin-left: 58.667px;
  }

  .p5-service-visual__platform img:last-child {
    width: 234.667px;
    height: 480px;
  }

  .p5-page--zh-cn .p5-upgrade,
  .p5-page--zh-cn .p5-services {
    --p5-story-step: 2053.333px;
  }

  .p5-page--zh-cn .p5-upgrade .p5-story-viewport,
  .p5-page--zh-cn .p5-services .p5-story-viewport {
    height: 2053.333px;
  }

  .p5-page--zh-cn .p5-upgrade-stage,
  .p5-page--zh-cn .p5-service-stage {
    transform: none;
  }

  .p5-page--zh-cn .p5-features > .p5-section-heading,
  .p5-page--zh-cn .p5-design > .p5-section-heading,
  .p5-page--zh-cn .p5-services > .p5-section-heading {
    height: 341px;
  }

  .p5-page--zh-cn .p5-features {
    min-height: 1631px;
  }

  .p5-page--zh-cn .p5-design-card--chassis {
    height: 1089px;
  }

  .p5-page--zh-cn .p5-design-card--screen {
    height: 1092px;
  }

  .p5-page--zh-cn .p5-design-card--turntable {
    height: 722px;
  }
}

@media (max-width: 768px) {
  .p5-page {
    --p5-gutter: 24px;
    padding-top: 70px;
  }

  .p5-subnav {
    height: var(--p5-subnav-height);
  }

  .p5-subnav__inner {
    width: 100%;
    padding: 0 12px;
    column-gap: 14px;
  }

  .p5-subnav__links {
    overflow-x: auto;
    justify-content: flex-start;
    gap: 20px;
    scrollbar-width: none;
  }

  .p5-subnav__links::-webkit-scrollbar {
    display: none;
  }

  .p5-subnav__links > a:not(.p5-subnav__cta) {
    font-size: 12px;
  }

  .p5-hero {
    height: 660px;
  }

  .p5-hero__background {
    height: 78%;
    object-position: center;
  }

  .p5-hero__copy {
    padding: 72px 20px 0;
  }

  .p5-hero__copy p {
    font-size: 15px;
  }

  .p5-hero__copy h1 {
    font-size: clamp(36px, 11vw, 46px);
  }

  .p5-hero__product {
    bottom: 13%;
    width: min(92%, 440px);
  }

  .p5-section-heading {
    height: auto;
    min-height: 260px;
    padding: 80px var(--p5-gutter) 64px;
  }

  .p5-section-heading > p {
    font-size: 18px;
  }

  .p5-section-heading h2 {
    margin-top: 10px;
    font-size: 30px;
  }

  .p5-upgrade > .p5-section-heading,
  .p5-services > .p5-section-heading {
    padding-bottom: 64px;
  }

  .p5-story-track {
    height: auto;
  }

  .p5-story-viewport {
    position: static;
    height: auto;
  }

  .p5-upgrade-stage {
    position: static;
    top: auto;
    height: auto;
    min-height: 0;
    display: block;
  }

  .p5-upgrade-accordion {
    height: auto;
    padding: 24px var(--p5-gutter) 34px;
    border-right: 0;
  }

  .p5-upgrade-accordion__trigger {
    min-height: 78px;
    padding: 18px 0;
    font-size: 16px;
  }

  .p5-upgrade-accordion__panel {
    padding-bottom: 28px;
  }

  .p5-page .p5-upgrade-accordion__mobile-image {
    width: 100%;
    height: clamp(220px, 68vw, 320px);
    margin-bottom: 24px;
    display: block;
  }

  .p5-upgrade-accordion__panel h3 {
    font-size: 20px;
  }

  .p5-upgrade-accordion__panel ul {
    margin-top: 14px;
    font-size: 15px;
    line-height: 1.45;
  }

  .p5-upgrade-controls {
    display: none;
  }

  .p5-upgrade-visual {
    display: none;
  }

  .p5-features {
    min-height: 0;
    padding-bottom: 80px;
  }

  .p5-feature-grid {
    min-height: 0;
    margin-bottom: 0;
    grid-template-columns: 1fr;
    grid-template-rows: none;
    grid-auto-rows: 420px;
    grid-template-areas:
      'bia'
      'posture'
      'agility'
      'balance'
      'blood'
      'vital';
    gap: 16px;
  }

  .p5-feature-card h3 {
    right: 24px;
    bottom: 24px;
    left: 24px;
    font-size: 21px;
  }

  .p5-feature-card--posture .p5-feature-card__copy {
    right: 24px;
    bottom: 24px;
    left: 24px;
  }

  .p5-feature-card--bia img {
    width: auto;
    height: 79%;
  }

  .p5-feature-card--posture img {
    height: 82%;
  }

  .p5-feature-card--vital img {
    object-position: center;
  }

  .p5-design-card {
    height: auto !important;
    min-height: 650px;
    padding: 64px var(--p5-gutter) 0 !important;
    display: flex;
    flex-direction: column;
  }

  .p5-design-card__copy {
    width: 100%;
  }

  .p5-design-card h3 {
    font-size: clamp(32px, 10vw, 44px);
  }

  .p5-design-card__copy p {
    margin-top: 14px;
    font-size: 16px;
    line-height: 1.45;
  }

  .p5-design-card > img {
    position: relative;
    inset: auto;
    width: 100%;
    height: 390px;
    margin-top: auto;
    object-fit: contain;
    transform: none;
  }

  .p5-design-card--turntable > img,
  .p5-design-card--contours > img {
    width: calc(100% + (2 * var(--p5-gutter)));
    max-width: none;
    margin-right: calc(-1 * var(--p5-gutter));
    margin-left: calc(-1 * var(--p5-gutter));
    object-fit: cover;
  }

  .p5-service-stage {
    position: static;
    top: auto;
    height: auto;
    min-height: 0;
    display: block;
  }

  .p5-service-controls,
  .p5-service-visual {
    display: none;
  }

  .p5-service-accordion {
    height: auto;
    padding: 24px var(--p5-gutter) 34px;
    display: block;
    border-right: 0;
  }

  .p5-service-accordion__trigger {
    min-height: 78px;
    padding: 18px 0;
    font-size: 16px;
  }

  .p5-service-accordion__panel {
    padding-bottom: 28px;
  }

  .p5-service-accordion__panel ul {
    font-size: 15px;
    line-height: 1.45;
  }

  .p5-service-accordion__mobile-visual {
    position: relative;
    width: 100%;
    height: clamp(260px, 72vw, 340px);
    margin-bottom: 24px;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .p5-service-accordion__mobile-visual .p5-service-visual__image {
    width: 100%;
    height: 100%;
  }

  .p5-service-accordion__mobile-visual .p5-service-visual__platform {
    width: min(100%, 430px);
    height: 100%;
    aspect-ratio: auto;
  }

  .p5-comparison__heading {
    height: auto;
    min-height: 230px;
    padding: 80px var(--p5-gutter) 60px;
  }

  .p5-comparison__heading h2 {
    font-size: 32px;
  }

  .p5-comparison__panel {
    min-height: 0;
    padding: 24px;
    grid-template-columns: 1fr;
    border-radius: 24px;
  }

  .p5-product-card {
    max-width: 416px;
    margin: 0 auto;
  }

  .p5-product-card + .p5-product-card {
    margin-top: 40px;
    padding-top: 40px;
    border-top: 1px solid rgba(255, 255, 255, 0.16);
  }

  .p5-product-card__functions {
    height: auto;
    min-height: 0;
    margin-bottom: 32px !important;
  }

  .p5-product-card__functions li {
    min-height: 34px;
  }

  .p5-product-card dl > div {
    min-height: 130px;
  }

  .p5-subscribe {
    height: 370px;
    padding-top: 88px;
  }

  .p5-subscribe h2 {
    font-size: 34px;
  }

  .p5-subscribe > p {
    padding: 0 20px;
    font-size: 16px;
  }

  .p5-subscribe__field {
    margin-top: 36px;
  }

  .p5-disclaimer {
    margin-bottom: 64px;
    font-size: 11px;
    line-height: 1.45;
  }
}

@media (max-width: 700px) {
  :global(html.p5-route-active #header .navbar) {
    pointer-events: none;
  }

  :global(html.p5-route-active #header .navbar-brand),
  :global(html.p5-route-active #header .mobile-menu-toggle),
  :global(html.p5-route-active #header .menu-wrap) {
    pointer-events: auto;
  }

  :global(html.p5-route-active #header .menu-wrap.mobile-active) {
    opacity: 1;
    visibility: visible;
    transform: translateX(0);
  }

  .p5-subnav__brand {
    display: none;
  }

  .p5-page {
    padding-top: 50px;
  }

  .p5-subnav__inner {
    grid-template-columns: minmax(0, 1fr) auto;
    padding-left: 76px;
  }

  .p5-subnav__links {
    height: 100%;
  }
}

@media (max-width: 420px) {
  .p5-feature-grid {
    grid-auto-rows: 390px;
  }

  .p5-agility-visual {
    width: 250px;
    grid-template-columns: repeat(3, 76px);
    gap: 11px;
  }

  .p5-agility-visual img {
    width: 76px;
    height: 59px;
  }

  .p5-design-card {
    min-height: 620px;
  }

  .p5-design-card > img {
    height: 330px;
  }
}

@media (max-width: 768px) {
  :global(html.p5-route-active #header .navbar) {
    height: 44px;
    min-height: 44px;
  }

  :global(html.p5-route-active #header .mobile-menu-toggle) {
    position: absolute;
    top: 8px;
    right: 14px;
    left: auto;
    margin: 0;
  }

  .p5-page {
    --p5-gutter: 14px;
    --p5-subnav-height: 50px;
    padding-top: var(--global-header-height, 44px);
  }

  .p5-subnav {
    height: 50px;
  }

  .p5-subnav__inner {
    width: 100%;
    padding: 0 14px;
    grid-template-columns: minmax(0, 1fr) auto;
    column-gap: 14px;
  }

  .p5-subnav__actions {
    gap: 6px;
  }

  .p5-subnav__brand {
    display: block;
    font-size: 12px;
  }

  .p5-subnav__links {
    display: none;
  }

  .p5-subnav__cta {
    padding: 0 12px;
  }

  .p5-hero {
    height: 576px;
  }

  .p5-hero__canvas {
    position: relative;
    display: block;
    width: 100%;
    height: 100%;
  }

  .p5-hero__background {
    right: auto;
    bottom: 0;
    left: 50%;
    width: 914px;
    max-width: none;
    height: 400px;
    object-fit: fill;
    object-position: center;
    transform: translateX(-50%);
  }

  .p5-hero__copy {
    position: absolute;
    z-index: 3;
    inset: 0;
    padding: 0;
    pointer-events: none;
  }

  .p5-hero__copy p {
    position: absolute;
    top: 60px;
    left: 0;
    width: 100%;
    color: rgba(255, 255, 255, 0.6);
    -webkit-text-fill-color: rgba(255, 255, 255, 0.6);
    font-size: 11.378px !important;
    line-height: normal;
    white-space: nowrap;
  }

  .p5-hero__copy h1 {
    position: absolute;
    top: 80.62px;
    left: 0;
    width: 100%;
    margin: 0;
    font-size: clamp(23px, 6.02vw, 26px);
    line-height: normal;
    white-space: nowrap;
  }

  .p5-hero__product {
    top: 212px;
    bottom: auto;
    left: calc(50% + 15.5px);
    width: min(341px, calc(100% - 28px));
    height: auto;
    aspect-ratio: 341 / 302;
    object-fit: contain;
    transform: translateX(-50%);
  }

  .p5-section {
    scroll-margin-top: 150px;
  }

  .p5-section-heading,
  .p5-upgrade > .p5-section-heading,
  .p5-services > .p5-section-heading {
    height: 372px;
    min-height: 0;
    padding: 56px 14px 0;
    background: #191b1d;
    text-align: left;
  }

  .p5-services > .p5-section-heading {
    height: 319px;
  }

  .p5-section-heading > p {
    font-size: 24px;
    line-height: normal;
  }

  .p5-section-heading h2 {
    width: 100%;
    margin: 24px 0 0;
    font-size: clamp(34px, 9.26vw, 40px);
    line-height: 1.2;
  }

  .p5-mobile-story {
    display: block;
    background: #000;
  }

  .p5-mobile-upgrade-item,
  .p5-mobile-service-item {
    position: relative;
    width: 100%;
    overflow: hidden;
    background: #000;
  }

  .p5-mobile-upgrade-item {
    padding: 56px 14px 48px;
  }

  .p5-mobile-upgrade-item--lower {
    min-height: min(696px, 161.112vw);
  }

  .p5-mobile-upgrade-item--upper {
    min-height: min(728px, 168.519vw);
  }

  .p5-mobile-upgrade-item--grip {
    min-height: min(640px, 148.149vw);
  }

  .p5-mobile-story__label {
    width: min(100%, 310px);
    background: var(--p5-orange);
    background-clip: text;
    color: transparent;
    font-family: 'MiSans-Bold', 'Segoe UI', sans-serif;
    font-size: 18px !important;
    line-height: 1.3;
  }

  .p5-mobile-upgrade-item__visual {
    position: relative;
    width: calc(100% + 28px);
    height: 230px;
    margin: 40px -14px 0;
    overflow: hidden;
  }

  .p5-mobile-upgrade-item__visual img {
    position: absolute;
    max-width: none;
  }

  .p5-mobile-upgrade-item--upper .p5-mobile-upgrade-item__visual img {
    top: -52px;
    left: -26.85%;
    width: 157.51%;
    height: 383px;
    object-fit: cover;
  }

  .p5-mobile-upgrade-item--lower .p5-mobile-upgrade-item__visual img {
    top: -40px;
    left: calc(50% + 20px);
    width: 328px;
    height: 337px;
    object-fit: contain;
    object-position: center bottom;
    transform: translateX(-50%);
  }

  .p5-mobile-upgrade-item--grip .p5-mobile-upgrade-item__visual {
    margin-top: 34px;
  }

  .p5-mobile-upgrade-item--grip .p5-mobile-upgrade-item__visual img {
    top: -104px;
    right: -104px;
    width: 520px;
    height: 365px;
    object-fit: contain;
    transform: rotate(18.44deg);
  }

  .p5-mobile-story__copy {
    margin-top: 40px;
  }

  .p5-mobile-story__copy h3 {
    font-family: 'MiSans-Bold', 'Segoe UI', sans-serif;
    font-size: 24px;
    line-height: 1.3;
  }

  .p5-mobile-story__copy h3 + h3 {
    margin-top: 4px;
  }

  .p5-mobile-story__copy ul {
    margin-top: 12px;
    color: rgba(255, 255, 255, 0.6);
    font-size: 14px;
    line-height: 1.42;
  }

  .p5-mobile-story__copy li + li {
    margin-top: 3px;
  }

  .p5-features {
    min-height: 0;
    padding-bottom: 56px;
  }

  .p5-feature-grid {
    width: min(calc(100% - 28px), 404px);
    min-height: 0;
    aspect-ratio: 404 / 364.722;
    margin: 0 auto;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    grid-template-rows: repeat(3, minmax(0, 1fr));
    grid-auto-rows: auto;
    grid-template-areas:
      'bia posture agility'
      'bia posture balance'
      'blood vital vital';
    gap: clamp(6px, 1.73vw, 7.481px);
  }

  .p5-feature-card {
    border-radius: clamp(6px, 1.73vw, 7.481px);
  }

  .p5-feature-card h3,
  .p5-feature-card--posture .p5-feature-card__copy {
    right: 10px;
    bottom: 10px;
    left: 10px;
  }

  .p5-feature-card h3 {
    font-size: clamp(7.5px, 2.08vw, 9px);
    line-height: 1.2;
  }

  .p5-feature-card--bia img {
    top: 9px;
    width: auto;
    height: 75%;
  }

  .p5-feature-card--posture img {
    top: 5px;
    left: -42%;
    width: 158%;
    height: 78%;
  }

  .p5-feature-card--posture .p5-feature-card__copy p {
    display: none;
  }

  .p5-agility-visual {
    top: 13px;
    width: 78px;
    grid-template-columns: repeat(3, 24px);
    gap: 3px;
  }

  .p5-agility-visual img {
    width: 24px;
    height: 19px;
  }

  .p5-feature-card--balance img {
    height: 100%;
  }

  .p5-feature-card--blood img {
    top: 18px;
    width: 57%;
  }

  .p5-feature-card--vital img {
    object-position: center;
  }

  .p5-design-card {
    width: 100%;
    min-height: 0;
    padding: 56px 14px 0 !important;
    display: block;
  }

  .p5-design-card__copy {
    width: 100%;
    text-align: left;
  }

  .p5-design-card h3 {
    font-size: clamp(34px, 9.26vw, 40px);
    line-height: 1.2;
  }

  .p5-design-card__copy p {
    width: 100%;
    margin: 24px 0 0;
    font-size: 14px;
    line-height: 1.42;
  }

  .p5-design-card > img {
    position: absolute;
    max-width: none;
    margin: 0;
    transform: none;
  }

  .p5-design-card--chassis {
    height: min(1062px, 245.833vw) !important;
  }

  .p5-design-card--chassis img {
    bottom: 0;
    left: 14px;
    width: calc(100% - 28px);
    height: min(752px, 174.074vw);
    object-fit: cover;
  }

  .p5-design-card--screen {
    height: min(930px, 215.278vw) !important;
  }

  .p5-design-card--screen img {
    bottom: 0;
    left: 14px;
    width: calc(100% - 28px);
    height: min(639px, 147.917vw);
    object-fit: cover;
  }

  .p5-design-card--turntable {
    height: min(560px, 129.63vw) !important;
  }

  .p5-design-card--turntable img {
    right: auto;
    bottom: 0;
    left: 14px;
    width: calc(100% - 28px);
    height: min(216px, 50vw);
    object-fit: cover;
  }

  .p5-design-card--contours {
    height: min(697px, 161.343vw) !important;
  }

  .p5-design-card--contours img {
    bottom: 0;
    left: 0;
    width: 100%;
    height: min(387px, 89.583vw);
    object-fit: cover;
    object-position: center;
  }

  .p5-mobile-service-item {
    padding: 56px 14px 42px;
  }

  .p5-mobile-service-item--reports {
    min-height: min(658px, 152.315vw);
  }

  .p5-mobile-service-item--trends {
    min-height: min(640px, 148.149vw);
  }

  .p5-mobile-service-item--platform {
    min-height: min(683px, 158.102vw);
  }

  .p5-mobile-service-item__visual {
    position: relative;
    width: calc(100% + 28px);
    height: 315px;
    margin: 0 -14px;
  }

  .p5-mobile-service-item--trends .p5-mobile-service-item__visual {
    height: 278px;
  }

  .p5-mobile-service-item--platform .p5-mobile-service-item__visual {
    width: 100%;
    height: 321px;
    margin: 0;
  }

  .p5-mobile-service-item__visual > img {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }

  .p5-mobile-service-item .p5-mobile-story__copy {
    margin-top: 56px;
  }

  .p5-mobile-service-platform {
    position: relative;
    width: 100%;
    height: 100%;
  }

  .p5-mobile-service-platform img:first-child {
    width: 96%;
    height: 100%;
    margin-left: 4%;
    object-fit: contain;
  }

  .p5-mobile-service-platform img:last-child {
    position: absolute;
    bottom: 0;
    left: 0;
    width: 16%;
    height: auto;
  }

  .p5-comparison__heading {
    height: 218px;
    min-height: 0;
    padding: 56px 14px 0;
    text-align: left;
  }

  .p5-comparison__heading h2 {
    width: 100%;
    margin: 0;
    font-size: clamp(34px, 9.26vw, 40px);
    line-height: 1.2;
  }

  .p5-comparison__panel {
    width: 100%;
    min-height: 882px;
    padding: 0;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 0;
    border-radius: 0;
    overflow: visible;
  }

  .p5-product-card,
  .p5-product-card + .p5-product-card {
    width: 100%;
    max-width: none;
    margin: 0;
    padding: 0;
    border-top: 0;
  }

  .p5-product-card > img {
    width: min(100%, 216px);
    height: auto;
    margin: 0 auto;
    aspect-ratio: 1;
  }

  .p5-product-card__model {
    min-height: 69px;
  }

  .p5-product-card h3 {
    font-size: 24px;
  }

  .p5-comparison-selector__trigger {
    width: 28px;
    height: 28px;
    margin-left: 8px;
    flex-basis: 28px;
  }

  .p5-comparison-selector__menu {
    z-index: 40;
    width: 128px;
  }

  .p5-product-card__functions {
    height: 276px;
    min-height: 276px;
    margin: 0 !important;
    padding: 0 14px;
    font-size: 9px;
    line-height: 1.2;
  }

  .p5-product-card__functions li {
    min-height: 24px;
    padding: 0 2px;
    line-height: 1.05;
    overflow-wrap: anywhere;
  }

  .p5-product-card dl > div {
    min-height: 0;
    padding: 14px 14px 0;
  }

  .p5-product-card dl > div:nth-child(1) {
    height: 90px;
  }

  .p5-product-card dl > div:nth-child(2) {
    height: 109px;
  }

  .p5-product-card dl > div:nth-child(3) {
    height: 122px;
  }

  .p5-product-card dt {
    font-size: 14px;
    line-height: 1.3;
  }

  .p5-product-card dd {
    margin-top: 8px;
    font-size: 11px;
    line-height: 1.35;
  }

  .p5-subscribe {
    height: 299px;
    padding: 56px 14px 0;
  }

  .p5-subscribe h2 {
    font-size: 40px;
  }

  .p5-subscribe > p {
    margin-top: 8px;
    padding: 0;
    font-size: 18px;
  }

  .p5-subscribe__field {
    width: 100%;
    margin-top: 48px;
  }

  .p5-disclaimer {
    min-height: 216px;
    margin-bottom: 0;
    padding: 28px 14px 32px;
    font-size: 10px;
    line-height: 1.35;
  }
}

@media (prefers-reduced-motion: reduce) {
  .p5-upgrade-accordion__panel--motion,
  .p5-upgrade-visual__image--motion,
  .p5-service-accordion__panel--motion,
  .p5-service-visual__content--motion {
    animation: none;
  }
}
</style>
